import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, BACKEND_URL, CATEGORIES, CATEGORY_NAME, categoryColor, DEAD_SPOT_COLOR, fmtTime, fmtDuration, fmtBytes } from "@/lib/api";
import {
  Upload, Play, Pause, Scissors, Sparkles, Trash2, Save, Check,
  X, ChevronLeft, ChevronRight, Film, Loader2, Wand2, Plus,
  ThumbsUp, ThumbsDown, HelpCircle, Link2, Pencil, ZoomIn, ZoomOut, Minimize2, Crosshair, Mic, Eraser, Undo2, Redo2, Tag, Video, FolderHeart,
} from "lucide-react";
import UrlFetchPanel from "@/components/UrlFetchPanel";
import AutocompleteInput from "@/components/AutocompleteInput";
import RenameModal, { RenamePencilButton } from "@/components/RenameModal";
import { AssignGalleryButton } from "@/components/ClipCard";

export default function Studio() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [cuts, setCuts] = useState([]);
  const [uploadCategory, setUploadCategory] = useState("barrel_racing");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState("");

  const loadVideos = () => api.get("/videos").then((r) => setVideos(r.data));

  // load list
  useEffect(() => {
    loadVideos();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!videoId) {
      setCurrentVideo(null);
      setCuts([]);
      return;
    }
    api.get(`/videos/${videoId}`).then((r) => setCurrentVideo(r.data));
    api.get(`/videos/${videoId}/cuts`).then((r) => setCuts(r.data));
  }, [videoId]);

  const refreshCuts = () => api.get(`/videos/${videoId}/cuts`).then((r) => setCuts(r.data));
  const refreshVideo = () => api.get(`/videos/${videoId}`).then((r) => setCurrentVideo(r.data));

  const probeDuration = (file) =>
    new Promise((resolve) => {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.src = URL.createObjectURL(file);
      v.onloadedmetadata = () => {
        const d = v.duration;
        URL.revokeObjectURL(v.src);
        resolve(isFinite(d) ? d : 0);
      };
      v.onerror = () => resolve(0);
    });

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);

    let videoId = null;
    try {
      const duration = await probeDuration(file);

      // 1) initiate multipart
      const initRes = await api.post("/videos/initiate-upload", {
        filename: file.name,
        file_size: file.size,
        content_type: file.type || "video/mp4",
        category: uploadCategory,
        duration,
        event_name: eventName.trim(),
        event_date: eventDate,
      });
      videoId = initRes.data.video_id;
      const { chunk_size, total_parts } = initRes.data;

      // 2) upload each chunk directly to S3
      const parts = [];
      for (let i = 0; i < total_parts; i++) {
        const partNumber = i + 1;
        const start = i * chunk_size;
        const end = Math.min(start + chunk_size, file.size);
        const blob = file.slice(start, end);

        const urlRes = await api.post("/videos/upload-part-url", {
          video_id: videoId,
          part_number: partNumber,
        });

        const putRes = await fetch(urlRes.data.url, {
          method: "PUT",
          body: blob,
        });
        if (!putRes.ok) {
          throw new Error(`Part ${partNumber} upload failed: ${putRes.status}`);
        }
        const etag = (putRes.headers.get("etag") || putRes.headers.get("ETag") || "").replaceAll('"', "");
        if (!etag) {
          throw new Error(`Part ${partNumber} missing ETag (check S3 CORS ExposeHeaders)`);
        }
        parts.push({ PartNumber: partNumber, ETag: etag });
        setUploadProgress(Math.round(((i + 1) / total_parts) * 100));
      }

      // 3) complete
      const completeRes = await api.post("/videos/complete-upload", {
        video_id: videoId,
        parts,
        duration,
      });
      toast.success("Video uploaded to S3");
      navigate(`/studio/${completeRes.data.id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Upload failed";
      toast.error("Upload failed: " + msg);
      // best-effort abort
      if (videoId) {
        api.post(`/videos/${videoId}/abort-upload`).catch(() => {});
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (!videoId) {
    return (
      <div className="px-6 md:px-12 py-10 max-w-[1100px]" data-testid="studio-upload-page">
        <div className="border-l-4 border-orange-600 pl-5 mb-10">
          <div className="label-tiny mb-2">Studio</div>
          <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">Start a cut session</h1>
          <p className="text-stone-400 mt-3 max-w-xl">
            Upload a rodeo video to begin marking cuts. MP4 recommended. Sample 720p test files work fastest in this preview environment.
          </p>
        </div>

        <div className="border border-stone-800 p-6 mb-8">
          <div className="label-tiny mb-3">Primary Event Category</div>
          <div className="flex flex-wrap gap-2 mb-6" data-testid="upload-category-buttons">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setUploadCategory(c.id)}
                className={`px-3 py-2 text-xs uppercase tracking-wider border transition-colors ${
                  uploadCategory === c.id
                    ? "bg-orange-700 border-orange-600 text-white"
                    : "border-stone-700 text-stone-400 hover:border-stone-500"
                }`}
                data-testid={`cat-btn-${c.id}`}
              >
                {c.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5" data-testid="event-fields">
            <div>
              <label className="label-tiny block mb-1">Performance (optional)</label>
              <AutocompleteInput
                field="event_name"
                value={eventName}
                onChange={setEventName}
                placeholder="e.g. Olds Open Jackpot · Round 1"
                className="field-input"
                data-testid="event-name-input"
              />
            </div>
            <div>
              <label className="label-tiny block mb-1">Performance Date (optional)</label>
              <input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="field-input"
                data-testid="event-date-input"
              />
            </div>
          </div>

          <label
            className="block border-2 border-dashed border-stone-700 hover:border-orange-700 p-12 text-center cursor-pointer transition-colors"
            data-testid="upload-dropzone"
          >
            <input
              type="file"
              accept="video/*"
              className="hidden"
              onChange={handleUpload}
              disabled={uploading}
              data-testid="upload-input"
            />
            {uploading ? (
              <div>
                <Loader2 size={28} className="mx-auto text-orange-500 animate-spin mb-3" />
                <div className="font-display uppercase text-xl">Uploading… {uploadProgress}%</div>
                <div className="w-full h-1 bg-stone-800 mt-4 max-w-md mx-auto">
                  <div className="h-full bg-orange-600" style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            ) : (
              <div>
                <Upload size={32} className="mx-auto text-stone-500 mb-3" />
                <div className="font-display uppercase text-xl text-stone-200">Drop video here or click to browse</div>
                <div className="text-xs text-stone-500 mt-2">MP4 / MOV · large files take time</div>
              </div>
            )}
          </label>

          <div className="my-4 flex items-center gap-3 text-stone-600 text-xs uppercase tracking-widest">
            <div className="flex-1 h-px bg-stone-800" /> or paste a URL <div className="flex-1 h-px bg-stone-800" />
          </div>

          <UrlFetchPanel
            compact
            defaultCategory={uploadCategory}
            onComplete={(id) => navigate(`/studio/${id}`)}
          />
        </div>

        {/* Existing videos */}
        {videos.length > 0 && (
          <div>
            <div className="flex items-end justify-between mb-4 border-b border-stone-800 pb-3">
              <h2 className="font-display uppercase text-xl font-bold">Continue editing</h2>
              <span className="label-tiny">{videos.length} video{videos.length !== 1 ? "s" : ""}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {videos.map((v) => (
                <VideoTile key={v.id} video={v} onOpen={() => navigate(`/studio/${v.id}`)} onChanged={loadVideos} />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!currentVideo) {
    return (
      <div className="p-12 text-center text-stone-500" data-testid="loading-video">
        <Loader2 className="mx-auto animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <Editor video={currentVideo} cuts={cuts} refreshCuts={refreshCuts} refreshVideo={refreshVideo} />
  );
}

// ============= EDITOR =============

function Editor({ video, cuts, refreshCuts, refreshVideo }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const videoRef = useRef(null);
  const timelineScrollRef = useRef(null);
  const timelineRef = useRef(null);
  // If the user came here from a Library clip's "Edit in Studio" button, we
  // may need to auto-seek + pre-fill mark-in/out. Consumed once on the first
  // onLoadedMetadata so a manual seek later isn't reset.
  const pendingSeekAppliedRef = useRef(false);

  // Set to true when the <video> element fires onError, i.e. the browser
  // cannot decode the file (typically MPEG-1/2, WMV, MKV etc.). Surfaces a
  // "Convert to MP4" banner over the player.
  const [videoError, setVideoError] = useState(false);

  // Drag-trim state for cuts on the main timeline.
  // mode: 'in' = drag left handle, 'out' = drag right handle, 'move' = slide
  const [dragCut, setDragCut] = useState(null);
  // When dragging, we display these live bounds instead of the DB ones so the
  // user sees the cut block move smoothly under their pointer.
  const [liveBounds, setLiveBounds] = useState(null); // {cut_id, start, end}
  // Right-click context menu over a timeline cut
  const [cutMenu, setCutMenu] = useState(null); // {cut_id, x, y}
  const stopAtRef = useRef(null);  // pause when current >= this time
  // Distinguish a "click" from a real drag on cut regions. A drag must move
  // beyond this many pixels before we start trimming/moving — otherwise a
  // click on a cut region seeks to the click position (like the empty timeline).
  const DRAG_THRESHOLD_PX = 4;
  const dragMovedRef = useRef(false);
  // Scoreboard OCR — auto-fires once per cut per session. Cuts already scanned
  // on the server (scoreboard_scanned=true) are skipped so page reloads don't
  // re-hit Gemini. Starts uninitialized so the first `cuts` payload seeds the
  // set without triggering scans on already-existing cuts.
  const scoreboardFiredRef = useRef(new Set());
  const scoreboardSeenInitialLoadRef = useRef(false);
  const [scoreboardBusy, setScoreboardBusy] = useState({}); // { [cut_id]: true }

  const fireScoreboardScan = useCallback(async (cutId, opts = {}) => {
    if (!cutId) return;
    if (!opts.force && scoreboardFiredRef.current.has(cutId)) return;
    scoreboardFiredRef.current.add(cutId);
    setScoreboardBusy((m) => ({ ...m, [cutId]: true }));
    try {
      const r = await api.post(`/cuts/${cutId}/scoreboard-scan`);
      const applied = r.data?.applied || {};
      const appliedFields = Object.keys(applied);
      if (appliedFields.length > 0) {
        // Nice, non-noisy toast — only surface if we actually filled something
        const rider = applied.rider_horse_name;
        const horse = applied.horse_name;
        const sub = applied.sub_label;
        const bits = [rider && `rider "${rider}"`, horse && `horse "${horse}"`, sub].filter(Boolean);
        toast.success(`Scoreboard read · ${bits.join(" · ")}`);
        refreshCuts();
      } else if (opts.showEmpty) {
        toast.info("No scoreboard graphic detected in this cut");
      }
    } catch (e) {
      // silent — retry allowed
      scoreboardFiredRef.current.delete(cutId);
      if (opts.showEmpty) {
        toast.error("Scoreboard read failed: " + (e?.response?.data?.detail || e.message));
      }
    } finally {
      setScoreboardBusy((m) => { const n = { ...m }; delete n[cutId]; return n; });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Undo / Redo stacks for cut edits in this session. Each entry is an
  // "operation" with inverse data so we can replay the API call.
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  // Push an operation that just happened
  const pushUndo = (op) => {
    setUndoStack((s) => [...s.slice(-19), op]);
    setRedoStack([]);  // any new action invalidates redo history
  };

  // Apply the inverse of an operation. Returns the redo entry.
  const applyInverse = async (op) => {
    if (op.type === "trim") {
      await api.put(`/cuts/${op.cut_id}`, op.before);
      return { ...op };
    }
    if (op.type === "delete_cut") {
      // Re-create the cut. The id changes — track new id for redo.
      const { snapshot } = op;
      const payload = {
        start_time: snapshot.start_time,
        end_time: snapshot.end_time,
        category: snapshot.category,
        sub_label: snapshot.sub_label || "",
        notes: snapshot.notes || "",
        rider_horse_name: snapshot.rider_horse_name || "",
        ai_suggested: !!snapshot.ai_suggested,
      };
      const r = await api.post(`/videos/${video.id}/cuts`, payload);
      return { ...op, restored_cut_id: r.data?.id };
    }
    if (op.type === "create_cut") {
      await api.delete(`/cuts/${op.cut_id}`);
      return { ...op };
    }
    if (op.type === "create_cut_batch") {
      // Delete every cut we created in this batch. Best-effort — ignore
      // 404s in case the user manually deleted one before undo.
      for (const id of (op.cut_ids || [])) {
        try { await api.delete(`/cuts/${id}`); } catch {}
      }
      return { ...op };
    }
    throw new Error("Unknown op type: " + op.type);
  };

  const applyForward = async (op) => {
    if (op.type === "trim") {
      await api.put(`/cuts/${op.cut_id}`, op.after);
      return { ...op };
    }
    if (op.type === "delete_cut") {
      // Re-delete the (possibly newly-recreated) cut
      const id = op.restored_cut_id || op.snapshot?.id;
      if (id) await api.delete(`/cuts/${id}`);
      return { ...op };
    }
    if (op.type === "create_cut" || op.type === "create_cut_batch") {
      // Re-create from the inverse snapshot — but we may not have one.
      // Best effort: if we don't have the cut data we just no-op.
      return { ...op };
    }
    throw new Error("Unknown op type: " + op.type);
  };

  const undo = async () => {
    if (undoStack.length === 0) return;
    const op = undoStack[undoStack.length - 1];
    try {
      const redoEntry = await applyInverse(op);
      setUndoStack((s) => s.slice(0, -1));
      setRedoStack((s) => [...s, redoEntry]);
      refreshCuts();
      toast.success(`Undid: ${op.type.replace("_", " ")}`);
    } catch (err) {
      toast.error("Undo failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  const redo = async () => {
    if (redoStack.length === 0) return;
    const op = redoStack[redoStack.length - 1];
    try {
      const undoEntry = await applyForward(op);
      setRedoStack((s) => s.slice(0, -1));
      setUndoStack((s) => [...s, undoEntry]);
      refreshCuts();
      toast.success(`Redid: ${op.type.replace("_", " ")}`);
    } catch (err) {
      toast.error("Redo failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  // Keyboard shortcuts: I=Set IN, O=Set OUT, Space=play/pause, Ctrl+Z=undo,
  // Ctrl+Shift+Z or Ctrl+Y=redo. Skipped when typing in a form field.
  // NOTE: reads playhead directly from videoRef.current so we don't need to
  // list `current` state in deps (which was declared below → caused a TDZ crash).
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable) return;
      const mod = e.metaKey || e.ctrlKey;
      // Undo / Redo
      if (mod && e.key === "z" && !e.shiftKey) { e.preventDefault(); undo(); return; }
      if (mod && ((e.key === "z" && e.shiftKey) || e.key === "y")) { e.preventDefault(); redo(); return; }
      // Set IN / OUT — single keystroke, no modifier
      if (!mod && !e.altKey) {
        const k = e.key.toLowerCase();
        const t = videoRef.current?.currentTime || 0;
        if (k === "i") {
          e.preventDefault();
          setMarkIn(Number(t.toFixed(2)));
          toast.success(`IN → ${fmtTime(t)}`, { duration: 900 });
        } else if (k === "o") {
          e.preventDefault();
          setMarkOut(Number(t.toFixed(2)));
          toast.success(`OUT → ${fmtTime(t)}`, { duration: 900 });
        } else if (k === "q") {
          // Queue the current IN/OUT and clear markers for the next cut
          e.preventDefault();
          queueRangeRef.current?.();
        } else if (e.key === " " || e.code === "Space") {
          // Play/pause with spacebar
          e.preventDefault();
          const v = videoRef.current;
          if (v) { if (v.paused) v.play(); else v.pause(); }
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undoStack, redoStack]);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(video.duration || 0);
  const [markIn, setMarkIn] = useState(null);
  const [markOut, setMarkOut] = useState(null);
  // Batch queue — hold multiple IN/OUT ranges before persisting them all in one shot.
  // Each entry: { id: string, start: number, end: number }.
  const [pendingRanges, setPendingRanges] = useState([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiProgress, setAiProgress] = useState(null); // { stage, note, elapsed, jobId, canCancel }
  const [generating, setGenerating] = useState(false);
  const [generatingCutId, setGeneratingCutId] = useState(null);
  const [clipsByCutId, setClipsByCutId] = useState(new Map());
  const [showEventEdit, setShowEventEdit] = useState(false);
  const [showVideoRename, setShowVideoRename] = useState(false);
  const [renamingCut, setRenamingCut] = useState(null); // { id, sub_label } | null
  const [showTrackRider, setShowTrackRider] = useState(false);
  const [showOverlayModal, setShowOverlayModal] = useState(false);
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [showPublishMaster, setShowPublishMaster] = useState(false);
  const [showSendAllClips, setShowSendAllClips] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showHelp, setShowHelp] = useState(() => {
    return localStorage.getItem("rcs_help_dismissed") !== "1";
  });

  // form. Fall back to the user's last picked category if the video itself
  // has no preference set, so the choice "sticks" across videos and sessions.
  const [formCategory, setFormCategoryRaw] = useState(
    video.category
    || localStorage.getItem("rcs_last_category")
    || "barrel_racing"
  );
  const setFormCategory = (cat) => {
    setFormCategoryRaw(cat);
    try { localStorage.setItem("rcs_last_category", cat); } catch {}
    // Persist as the video's default too so reopening this video keeps the choice
    if (cat && cat !== video.category) {
      api.patch(`/videos/${video.id}`, { category: cat }).catch(() => {});
    }
  };
  const [formSubLabel, setFormSubLabel] = useState("");
  const [formAssociation, setFormAssociation] = useState(video?.association || "");
  const [formEventName, setFormEventName] = useState(video?.event_name || "");
  const [formPerformance, setFormPerformance] = useState("");
  const [formRider, setFormRider] = useState("");
  const [formHeeler, setFormHeeler] = useState("");
  const [formHorse, setFormHorse] = useState("");
  const [formOwner, setFormOwner] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [formTime, setFormTime] = useState("");   // run time seconds, string in the input
  const [formScore, setFormScore] = useState(""); // judge score, string in the input

  // Fetch which cuts are already clipped (so we can mark them done).
  // Stores the FULL clip object per cut so downstream UI (Assign popover)
  // can show current association / event / rider without an extra fetch.
  const refreshGenerated = useCallback(async () => {
    try {
      const r = await api.get("/clips", { params: { video_id: video.id } });
      const m = new Map();
      for (const c of (r.data || [])) {
        if (c.cut_id) m.set(c.cut_id, c);
      }
      setClipsByCutId(m);
    } catch {}
  }, [video.id]);

  useEffect(() => { refreshGenerated(); }, [refreshGenerated, cuts.length]);

  // Auto-fire scoreboard OCR on newly-appeared cuts.
  //  • First load  → mark every cut as "seen" so we don't re-scan on refresh.
  //  • Later loads → any cut that is NEW (unseen) *and* not yet marked
  //    `scoreboard_scanned` on the server gets scanned once.
  useEffect(() => {
    if (!cuts) return;
    if (!scoreboardSeenInitialLoadRef.current) {
      // Prime the fired set with the cuts already on record so we don't
      // spam Gemini after page navigation.
      cuts.forEach((c) => {
        if (c?.id) scoreboardFiredRef.current.add(c.id);
      });
      scoreboardSeenInitialLoadRef.current = true;
      return;
    }
    cuts.forEach((c) => {
      if (!c?.id) return;
      if (scoreboardFiredRef.current.has(c.id)) return;
      if (c.scoreboard_scanned) {
        scoreboardFiredRef.current.add(c.id);
        return;
      }
      if (c.is_dead_spot) {
        scoreboardFiredRef.current.add(c.id);
        return;
      }
      // Skip cuts that already have rider AND horse filled — nothing to add
      if ((c.rider_horse_name || "").trim() && (c.horse_name || "").trim()) {
        scoreboardFiredRef.current.add(c.id);
        return;
      }
      fireScoreboardScan(c.id);
    });
  }, [cuts, fireScoreboardScan]);

  const onPlayPause = () => {
    if (!videoRef.current) return;
    clearStopAt();
    if (videoRef.current.paused) videoRef.current.play();
    else videoRef.current.pause();
  };

  // Sync playback rate to <video>
  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = playbackRate;
  }, [playbackRate]);

  // Auto-scroll the timeline to keep the playhead visible when zoomed in.
  // Only fires while playing AND only when the playhead actually leaves the
  // viewport — prevents the timeline from snapping under you while scrubbing.
  useEffect(() => {
    if (zoom <= 1) return;
    if (!playing) return;
    const el = timelineScrollRef.current;
    if (!el || !duration) return;
    const totalWidth = el.scrollWidth;
    const viewport = el.clientWidth;
    const playheadX = (current / duration) * totalWidth;
    const visibleStart = el.scrollLeft;
    const visibleEnd = visibleStart + viewport;
    // Comfort zone: 20% padding inside the viewport edges
    const pad = viewport * 0.2;
    if (playheadX < visibleStart + pad || playheadX > visibleEnd - pad) {
      const target = playheadX - viewport / 2;
      const max = totalWidth - viewport;
      const clamped = Math.max(0, Math.min(max, target));
      el.scrollLeft = clamped;
    }
  }, [current, zoom, duration, playing]);

  const seek = (t, autoplay = false, stopAt = null) => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, Math.min(duration, t));
      stopAtRef.current = stopAt;
      if (autoplay) videoRef.current.play();
    }
  };

  // Manual play/seek/etc. clears the stop-at fence so only "play cut" auto-pauses
  const clearStopAt = () => { stopAtRef.current = null; };

  // Queue the current IN/OUT as a pending range so the user can mark multiple
  // clips before persisting them all in one batch (shortcut: Q).
  const queueRange = () => {
    if (markIn == null || markOut == null) {
      toast.error("Set IN and OUT first");
      return;
    }
    if (markOut <= markIn) {
      toast.error("OUT must be after IN");
      return;
    }
    if (markOut - markIn < 0.3) {
      toast.error("Range too short (minimum 0.3s)");
      return;
    }
    const range = { id: `p-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, start: markIn, end: markOut };
    setPendingRanges((prev) => [...prev, range]);
    setMarkIn(null);
    setMarkOut(null);
    toast.success(`Queued ${fmtDuration(range.end - range.start)} · ${pendingRanges.length + 1} range${pendingRanges.length + 1 !== 1 ? "s" : ""} pending`);
  };
  // Keep a ref to the latest queueRange so keyboard shortcuts (which are
  // registered against stable state deps) can call the current-render closure.
  const queueRangeRef = useRef(queueRange);
  useEffect(() => { queueRangeRef.current = queueRange; });

  const removePendingRange = (id) => {
    setPendingRanges((prev) => prev.filter((r) => r.id !== id));
  };

  const handleAddCut = async () => {
    // Build the list of ranges to persist: everything queued PLUS the current
    // IN/OUT pair if the user has both marked.  If the queue is empty AND
    // the marks aren't set, error.
    const ranges = [...pendingRanges];
    if (markIn != null && markOut != null) {
      if (markOut <= markIn) {
        toast.error("Current OUT must be after IN");
        return;
      }
      ranges.push({ id: "current", start: markIn, end: markOut });
    }
    if (ranges.length === 0) {
      toast.error("Set IN and OUT markers (or queue some ranges first)");
      return;
    }

    // POST them in sequence so cut_id ordering in the DB matches the order
    // the user marked them on the timeline.
    let created = 0;
    const createdIds = [];
    // Parse the optional time / score inputs — accept blank as null.
    const parsedTime = formTime.trim() === "" ? null : (isNaN(parseFloat(formTime)) ? null : parseFloat(formTime));
    const parsedScore = formScore.trim() === "" ? null : (isNaN(parseFloat(formScore)) ? null : parseFloat(formScore));
    try {
      for (const r of ranges) {
        const res = await api.post(`/videos/${video.id}/cuts`, {
          start_time: r.start,
          end_time: r.end,
          category: formCategory,
          association: formAssociation,
          event_name: formEventName,
          performance: formPerformance,
          performance_name: formEventName,
          sub_label: formSubLabel,
          notes: formNotes,
          rider_horse_name: formRider,
          horse_name: formHorse,
          heeler_name: formHeeler,
          owner_name: formOwner,
          run_time: parsedTime,
          score: parsedScore,
          ai_suggested: false,
        });
        createdIds.push(res.data?.id);
        created++;
      }
      // Group them into a single undo entry so Ctrl+Z rolls back the whole batch
      pushUndo({ type: "create_cut_batch", cut_ids: createdIds });
      toast.success(`${created} cut${created !== 1 ? "s" : ""} added (Ctrl+Z to undo)`);
      setMarkIn(null);
      setMarkOut(null);
      setPendingRanges([]);
      setFormSubLabel("");
      setFormRider("");
      setFormHeeler("");
      setFormHorse("");
      setFormOwner("");
      setFormNotes("");
      setFormTime("");
      setFormScore("");
      refreshCuts();
    } catch (err) {
      toast.error(`Saved ${created}/${ranges.length}. Failed: ` + (err?.response?.data?.detail || err.message));
      refreshCuts();
    }
  };

  // Splice: destructively remove [markIn, markOut] from the raw source video.
  // Re-encodes on the backend and cascades all cuts. This is what "delete a
  // section from the raw video" means in-Studio.
  const [splicing, setSplicing] = useState(false);
  const handleSpliceRange = async () => {
    if (markIn == null || markOut == null) {
      toast.error("Set IN and OUT first — that's the range you want removed");
      return;
    }
    if (markOut <= markIn || markOut - markIn < 0.2) {
      toast.error("Range too short (need at least 0.2 s)");
      return;
    }
    const removed = markOut - markIn;
    const cutsHit = cuts.filter(
      (c) => Math.max(c.start_time, markIn) < Math.min(c.end_time, markOut),
    ).length;
    const cutsShifted = cuts.filter((c) => c.start_time >= markOut).length;
    const msg =
      `Remove ${fmtDuration(removed)} from the source video?\n\n` +
      `Range: ${fmtTime(markIn)} → ${fmtTime(markOut)}\n` +
      `Overlapping cuts to DELETE: ${cutsHit}\n` +
      `Later cuts to shift left: ${cutsShifted}\n\n` +
      `This RE-ENCODES the raw video and cannot be undone. Continue?`;
    if (!window.confirm(msg)) return;

    setSplicing(true);
    try {
      let resp;
      try {
        resp = await api.post(`/videos/${video.id}/splice`, { start: markIn, end: markOut });
      } catch (err) {
        // Auto-recover from a stuck splice_status left behind by a killed worker
        const detail = err?.response?.data?.detail || "";
        if (err?.response?.status === 409 && /already running/i.test(detail)) {
          if (window.confirm(
            "A previous splice appears to still be running on the server.\n\n" +
            "This can happen if the backend was restarted mid-encode. Reset it and start a new splice?"
          )) {
            resp = await api.post(`/videos/${video.id}/splice?force=true`, { start: markIn, end: markOut });
          } else {
            setSplicing(false);
            return;
          }
        } else {
          throw err;
        }
      }
      toast.info("Splicing raw video… this can take a few minutes on long footage.");
      // Poll video doc for splice_status
      const deadline = Date.now() + 30 * 60 * 1000;
      let lastStatus = "queued";
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 4000));
        try {
          const v = await api.get(`/videos/${video.id}`);
          const s = v.data.splice_status || "";
          const prog = v.data.splice_progress ?? 0;
          if (s !== lastStatus) {
            lastStatus = s;
            if (s === "encoding") toast.info("Re-encoding source…");
            else if (s === "uploading") toast.info(`Uploading trimmed source (${prog}%)…`);
            else if (s === "cascading") toast.info("Cascading cuts…");
          }
          if (s === "done") {
            const last = v.data.splice_last || {};
            toast.success(
              `Removed ${fmtDuration(removed)} from source · shifted ${last.cuts_shifted || 0} cut(s), deleted ${last.cuts_deleted || 0}`,
            );
            setMarkIn(null);
            setMarkOut(null);
            // Reload everything — duration + video src change
            window.location.reload();
            return;
          }
          if (s === "failed") {
            toast.error("Splice failed: " + (v.data.splice_error || "unknown"), { duration: 12000 });
            return;
          }
        } catch (pollErr) {
          // network hiccup — continue
        }
      }
      toast.warning("Splice is taking a while — refresh in a bit to see the result.");
    } catch (err) {
      toast.error("Splice failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSplicing(false);
    }
  };

  // Human-friendly labels for each stage the AI worker emits. Rough elapsed
  // budgets are just for the UI hint — the actual deadline is 45 min.
  const AI_STAGE_INFO = {
    starting:              { label: "Starting up",           hint: "Contacting Gemini…" },
    reading_source:        { label: "Reading source",        hint: "Fetching S3 metadata" },
    preparing_summary:     { label: "Preparing for Gemini",  hint: "ffmpeg is compressing the video into a summary. This is the slow part — expect ~1 min per 15 min of source." },
    analyzing_with_gemini: { label: "Gemini is watching",    hint: "The model is finding events. Usually 30–90 seconds." },
    done:                  { label: "Done",                  hint: "" },
    failed:                { label: "Failed",                hint: "" },
  };

  const handleAISuggest = async () => {
    setAiBusy(true);
    const started = Date.now();
    setAiProgress({ stage: "starting", note: "", elapsed: 0, jobId: null, canCancel: false });
    try {
      const start = await api.post(`/videos/${video.id}/ai-suggest`);
      const jobId = start.data.job_id;
      setAiProgress((p) => ({ ...(p || {}), jobId, canCancel: true }));

      // Poll every 5s, up to 45 min (long masters like Vimeo full-perfs take
      // 25–35 min just to ffmpeg-preprocess before Gemini can even start).
      const deadline = Date.now() + 45 * 60 * 1000;
      let lastStatus = "queued";
      let lastCount = 0;
      let consecutiveErrors = 0;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 5000));
        // Update elapsed every tick so the UI counter keeps moving even if
        // the stage hasn't changed
        setAiProgress((p) => p && ({ ...p, elapsed: Math.round((Date.now() - started) / 1000) }));
        let j;
        try {
          j = await api.get(`/ai-jobs/${jobId}`);
          consecutiveErrors = 0;
        } catch (pollErr) {
          consecutiveErrors += 1;
          if (consecutiveErrors < 6) continue;
          throw new Error(`Cannot reach the AI job status endpoint (${consecutiveErrors} consecutive failures — likely a Cloudflare/network issue). The AI job may still be running in the background; refresh in a minute to see the cuts.`);
        }
        const s = j.data.status;
        const stage = j.data.stage || "";
        const note = j.data.error || "";
        if (s !== lastStatus) {
          lastStatus = s;
          if (s === "running") toast.info("Gemini is analyzing the video…");
        }
        // Track suggested_count so we can refresh the timeline when new cuts stream in
        const newCount = j.data.suggested_count || 0;
        setAiProgress((p) => p && ({ ...p, stage, note, streamedCount: newCount }));
        if (newCount > lastCount) {
          // Cuts arrived — pull them so the timeline updates live
          refreshCuts();
          lastCount = newCount;
        }
        if (s === "done") {
          toast.success(`AI suggested ${j.data.suggested_count} cut${j.data.suggested_count !== 1 ? "s" : ""}`);
          refreshCuts();
          if (j.data.audio_job_id) {
            await awaitAudioJob(j.data.audio_job_id);
          }
          return;
        }
        if (s === "failed" || s === "cancelled") {
          const label = s === "cancelled" ? "cancelled" : "failed";
          toast.error(`AI ${label}: ` + (j.data.error || "unknown"), { duration: 12000 });
          return;
        }
      }
      // Client deadline hit — the worker may still be running on the server.
      let finalStatus = "unknown";
      let finalCuts = null;
      try {
        const j = await api.get(`/ai-jobs/${jobId}?lean=false`);
        finalStatus = j.data.status;
        finalCuts = j.data.suggested_count;
      } catch {}
      refreshCuts();
      if (finalStatus === "done") {
        toast.success(`AI finished (${finalCuts ?? "?"} cuts) — pulled from server after client timeout`);
      } else if (finalStatus === "failed" || finalStatus === "cancelled") {
        try {
          const j2 = await api.get(`/ai-jobs/${jobId}?lean=false`);
          toast.error(`AI ${finalStatus}: ` + (j2.data.error || "unknown"));
        } catch {
          toast.error(`AI job ${finalStatus} after client timeout — check the Jobs page`);
        }
      } else {
        toast.warning(
          `Poll timed out after 45 min — job is still ${finalStatus} on the server. Refresh the page in a minute to see if it finished.`,
          { duration: 12000 },
        );
      }
      return;
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg;
      if (detail) msg = detail;
      else if (status && status >= 520) msg = `Cloudflare error ${status} — the server dropped the request. Try again in a minute; if it keeps happening, contact Emergent support.`;
      else if (status) msg = `Server returned ${status}: ${err.message}`;
      else msg = err.message || "Unknown error";
      toast.error("AI failed: " + msg, { duration: 12000 });
    } finally {
      setAiBusy(false);
      setAiProgress(null);
    }
  };

  const cancelAiJob = async () => {
    if (!aiProgress?.jobId) return;
    try {
      await api.post(`/ai-jobs/${aiProgress.jobId}/cancel`);
      toast.success("Cancel signal sent — job will stop at the next stage check");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to cancel job");
    }
  };

  const [deepScanProgress, setDeepScanProgress] = useState(null); // {pct, completed, total, found}
  const [audioProgress, setAudioProgress] = useState(null); // {pct, stage, matched, scanned}

  // Polls an audio job, updates progress, and refreshes cut list when new riders are matched.
  // Used by manual "Identify Riders" AND auto-spawned audio jobs after AI Suggest / Deep Scan.
  const awaitAudioJob = async (jobId) => {
    setAudioProgress({ pct: 0, stage: "starting", matched: 0, scanned: 0 });
    let lastMatched = 0;
    const deadline = Date.now() + 30 * 60 * 1000;
    try {
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 4000));
        const j = await api.get(`/audio-jobs/${jobId}`);
        const d = j.data;
        setAudioProgress({
          pct: d.progress || 0,
          stage: d.stage || d.status || "running",
          matched: d.matched || 0,
          scanned: d.scanned || 0,
        });
        // Live-refresh cuts when new riders get filled in
        if ((d.matched || 0) > lastMatched) {
          lastMatched = d.matched || 0;
          refreshCuts();
        }
        if (d.status === "done") {
          if ((d.matched || 0) > 0) {
            toast.success(`Identified ${d.matched} rider${d.matched === 1 ? "" : "s"} from audio`);
          } else {
            toast.info("Audio scan finished — no new rider names extracted");
          }
          refreshCuts();
          return;
        }
        if (d.status === "failed") {
          toast.error("Audio ID failed: " + (d.error || "unknown"));
          return;
        }
      }
      toast.error("Audio ID timed out");
    } catch (err) {
      toast.error("Audio ID failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setAudioProgress(null);
    }
  };

  const handleIdentifyRiders = async () => {
    setAiBusy(true);
    toast.info("Transcribing announcer audio with Whisper…");
    try {
      const start = await api.post(`/videos/${video.id}/identify-riders`);
      await awaitAudioJob(start.data.job_id);
    } catch (err) {
      toast.error("Audio ID failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setAiBusy(false);
    }
  };

  const pollDeepScanJob = useCallback(async (jobId, opts = {}) => {
    const { resumed = false } = opts;
    setAiBusy(true);
    setDeepScanProgress({ pct: 0, completed: 0, total: 0, found: 0, jobId });
    if (resumed) toast.info("Reconnected to a Deep Scan already running on this video");
    try {
      const deadline = Date.now() + 60 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 4000));
        const j = await api.get(`/ai-jobs/${jobId}`);
        const d = j.data;
        setDeepScanProgress({
          pct: d.progress || 0,
          completed: d.completed_windows || 0,
          total: d.total_windows || 0,
          found: d.suggested_count || 0,
          jobId,
        });
        if (d.status === "done") {
          toast.success(`Deep Scan added ${d.suggested_count} new cut${d.suggested_count !== 1 ? "s" : ""}`);
          refreshCuts();
          setDeepScanProgress(null);
          if (d.audio_job_id) {
            await awaitAudioJob(d.audio_job_id);
          }
          return;
        }
        if (d.status === "failed") {
          toast.error("Deep Scan failed: " + (d.error || "unknown"));
          return;
        }
        if (d.status === "cancelled") {
          toast.info("Deep Scan cancelled");
          refreshCuts();
          return;
        }
      }
      toast.error("Deep Scan poll timed out — check the Jobs page for status");
    } catch (err) {
      toast.error("Deep Scan poll failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setAiBusy(false);
      setDeepScanProgress(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeepScan = async (preset = "runs") => {
    const dur = video?.duration || 0;
    const minutes = (dur / 60).toFixed(1);
    const PRESET_LABELS = {
      runs: "Deep Scan (runs & competitions)",
      bloopers: "Blooper Scan (buck-offs, wrecks, gate mishaps)",
      best_runs: "Highlight Scan (best runs only)",
      reactions: "Reaction Scan (celebrations, hat throws)",
      crowd_moments: "Crowd Scan (ovations, kids reacting)",
    };
    const label = PRESET_LABELS[preset] || "Deep Scan";
    if (!window.confirm(
      `${label} — splits this ${minutes}-min reel into 10-min windows and runs Gemini once per window. ` +
      `Takes ~1-2 min per window. Continue?`
    )) return;

    toast.info(`${label} started`);
    try {
      const start = await api.post(`/videos/${video.id}/ai-deep-scan`,
        { window_seconds: 600, scan_preset: preset });
      await pollDeepScanJob(start.data.job_id);
    } catch (err) {
      toast.error(`${label} failed: ` + (err?.response?.data?.detail || err.message));
    }
  };

  const handleCancelDeepScan = async () => {
    const jobId = deepScanProgress?.jobId;
    if (!jobId) return;
    try {
      await api.post(`/ai-jobs/${jobId}/cancel`);
      toast.info("Cancelling Deep Scan…");
      // The poll loop above will see status === 'cancelled' next tick and clean up
    } catch (err) {
      toast.error("Cancel failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  // Re-attach to any AI job that's still running on this video (e.g. after a
  // browser refresh mid-scan). This makes the progress panel + cancel button
  // appear automatically so the user is never stranded with a background job
  // they can't see or stop.
  useEffect(() => {
    if (!video?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/videos/${video.id}/active-ai-job`);
        const active = r.data?.active;
        if (!active || cancelled) return;
        if (active.mode === "deep-scan") {
          pollDeepScanJob(active.id, { resumed: true });
        }
        // (AI Suggest has its own resume path — kept out for now)
      } catch {
        // non-fatal — endpoint absent on old servers, just skip
      }
    })();
    return () => { cancelled = true; };
  }, [video?.id, pollDeepScanJob]);

  // Check whether the last deep-scan on this video died mid-way and is
  // resumable. Shown as a banner + button on the Studio page.
  const [resumeInfo, setResumeInfo] = useState(null);
  const refreshResumeInfo = useCallback(async () => {
    if (!video?.id) return;
    try {
      const r = await api.get(`/videos/${video.id}/deep-scan-resume-info`);
      setResumeInfo(r.data?.resumable ? r.data : null);
    } catch {
      setResumeInfo(null);
    }
  }, [video?.id]);
  useEffect(() => { refreshResumeInfo(); }, [refreshResumeInfo, deepScanProgress]);

  const handleResumeDeepScan = async () => {
    if (!resumeInfo) return;
    const fromWin = resumeInfo.resume_from_window;
    const total = resumeInfo.total_windows;
    if (!window.confirm(
      `Resume Deep Scan on windows ${fromWin + 1}–${total} of ${total}? ` +
      `Windows 1–${fromWin} were completed by the previous run and will be skipped.`
    )) return;
    toast.info(`Resuming Deep Scan from window ${fromWin + 1}/${total}…`);
    try {
      const start = await api.post(`/videos/${video.id}/ai-deep-scan-resume`);
      setResumeInfo(null);
      await pollDeepScanJob(start.data.job_id);
    } catch (err) {
      toast.error("Resume failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  const handleAcceptCut = async (cut) => {
    await api.put(`/cuts/${cut.id}`, { });
    // mark accepted via update — backend doesn't have accept flag in PUT, but generate uses accepted=true; we'll patch by deleting & recreating? Simpler: hit a specific endpoint or update separately
    // Use PUT with a workaround: set sub_label to itself and rely on generate-clips with cut_ids
    refreshCuts();
  };

  const handleDeleteCut = async (cut) => {
    // Snapshot the cut BEFORE deletion so undo can recreate it
    const snapshot = { ...cut };
    await api.delete(`/cuts/${cut.id}`);
    pushUndo({ type: "delete_cut", snapshot });
    toast.success("Cut deleted (Ctrl+Z to undo)");
    refreshCuts();
  };

  const handleFeedback = async (cut, feedback) => {
    const newFb = cut.feedback === feedback ? null : feedback;
    await api.put(`/cuts/${cut.id}/feedback`, { feedback: newFb });
    refreshCuts();
  };

  const handleGenerate = async (targetCutIds = null) => {
    const ungenerated = cuts.filter((c) => !clipsByCutId.has(c.id));
    const ids = targetCutIds || ungenerated.map((c) => c.id);
    if (ids.length === 0) {
      toast.info("All cuts already generated. Delete a cut and re-add to regenerate.");
      return;
    }
    const isSingle = ids.length === 1;
    if (isSingle) setGeneratingCutId(ids[0]);
    else setGenerating(true);
    try {
      const start = await api.post(`/videos/${video.id}/generate-clips`, { cut_ids: ids });
      const jobId = start.data.job_id;
      const total = start.data.total;
      if (!isSingle) toast.info(`Cutting ${total} clips… (~5-15s per clip)`);
      else toast.info("Cutting clip…");

      const deadline = Date.now() + 30 * 60 * 1000;
      let lastGen = -1;
      let lastFail = -1;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, isSingle ? 2000 : 4000));
        const j = await api.get(`/clip-jobs/${jobId}`);
        const { status, generated, failed, error, last_error } = j.data;
        if (!isSingle && generated !== lastGen) {
          lastGen = generated;
          toast.info(`Cut ${generated}/${total} done${failed ? ` · ${failed} failed` : ""}`);
        }
        // Surface each new per-cut failure as it happens so the user can
        // see WHY things are breaking mid-batch, not just a bare count.
        if (failed > lastFail && last_error) {
          lastFail = failed;
          toast.error(`Cut failed · ${last_error}`);
        }
        if (status === "done") {
          if (isSingle) {
            toast.success(failed ? "Clip generation failed" : "Clip generated");
          } else if (failed && !generated) {
            toast.error(`All ${failed} clips failed${error ? " · " + error : ""}`);
          } else if (failed) {
            toast.warning(`Done · ${generated} clips · ${failed} failed`);
          } else {
            toast.success(`Done · ${generated} clips`);
          }
          await refreshGenerated();
          if (!isSingle) navigate("/library");
          return;
        }
        if (status === "failed") {
          toast.error("Generation failed: " + (error || "unknown"));
          return;
        }
      }
      toast.error("Generation timed out");
    } catch (err) {
      toast.error("Generation failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setGenerating(false);
      setGeneratingCutId(null);
    }
  };

  // ---- Timeline drag-trim ----
  const computeTimeFromEvent = (clientX) => {
    const el = timelineRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return ratio * duration;
  };

  const onCutPointerDown = (cut, mode) => (e) => {
    if (generating || generatingCutId) return;
    e.preventDefault();
    e.stopPropagation();
    dragMovedRef.current = false;
    setDragCut({
      cut_id: cut.id,
      mode,
      origin_x: e.clientX,
      origin_start: cut.start_time,
      origin_end: cut.end_time,
      is_clipped: clipsByCutId.has(cut.id),
    });
    setLiveBounds({ cut_id: cut.id, start: cut.start_time, end: cut.end_time });
  };

  // Track pointer while dragging
  useEffect(() => {
    if (!dragCut) return;

    const onMove = (e) => {
      // Suppress jitter: don't start any drag/seek until the pointer has
      // actually moved past the threshold. Under the threshold this is
      // treated as a click and handled in onUp.
      if (!dragMovedRef.current) {
        if (Math.abs(e.clientX - dragCut.origin_x) < DRAG_THRESHOLD_PX) return;
        dragMovedRef.current = true;
      }
      const t = computeTimeFromEvent(e.clientX);
      let newStart = dragCut.origin_start;
      let newEnd = dragCut.origin_end;
      if (dragCut.mode === "in") {
        newStart = Math.max(0, Math.min(t, dragCut.origin_end - 0.3));
      } else if (dragCut.mode === "out") {
        newEnd = Math.max(dragCut.origin_start + 0.3, Math.min(t, duration));
      } else if (dragCut.mode === "move") {
        const tAtOrigin = computeTimeFromEvent(dragCut.origin_x);
        const delta = t - tAtOrigin;
        const span = dragCut.origin_end - dragCut.origin_start;
        newStart = Math.max(0, Math.min(dragCut.origin_start + delta, duration - span));
        newEnd = newStart + span;
      }
      setLiveBounds({ cut_id: dragCut.cut_id, start: newStart, end: newEnd });
      // Live-seek the source video so the user previews the new IN/OUT
      if (dragCut.mode === "in") seek(newStart);
      else if (dragCut.mode === "out") seek(Math.max(0, newEnd - 0.2));
      else seek(newStart);
    };

    const onUp = async () => {
      const live = liveBounds;
      const drag = dragCut;
      const wasMoved = dragMovedRef.current;
      dragMovedRef.current = false;
      setDragCut(null);
      setLiveBounds(null);
      if (!drag || !live) return;
      // Plain click (no real drag) — behavior depends on where the click landed:
      //   • Body ("move")    → treat as a timeline seek at the click position
      //                        AND scroll the right-sidebar card for this cut into view
      //   • Edge handle in/out → snap that edge to the current playhead (existing feature)
      if (!wasMoved) {
        if (drag.mode === "move") {
          clearStopAt();
          seek(computeTimeFromEvent(drag.origin_x));
          // Scroll the matching cut card into view + flash it so the user
          // instantly sees the "generated clip" on the right when they click
          // the cut on the timeline.
          try {
            const el = document.querySelector(`[data-testid="cut-item-${drag.cut_id}"]`);
            if (el) {
              el.scrollIntoView({ block: "center", behavior: "smooth" });
              el.classList.add("cut-card-flash");
              setTimeout(() => el.classList.remove("cut-card-flash"), 1200);
            }
          } catch { /* non-critical */ }
          return;
        }
        // fall through to click-to-snap for in/out handles below
      }
      let nextStart = live.start;
      let nextEnd = live.end;
      const moved =
        Math.abs(live.start - drag.origin_start) > 0.05 ||
        Math.abs(live.end - drag.origin_end) > 0.05;
      // CLICK-TO-SNAP: a quick click on an IN/OUT edge handle with no drag
      // snaps that edge to the current playhead — lets the user park the
      // playhead first and then "set it here" with one click.
      if (!moved && (drag.mode === "in" || drag.mode === "out")) {
        // Read the playhead directly from the video element so we don't have
        // to list `current` state in the effect deps (that caused the effect
        // to re-mount on every timeupdate, dropping drags mid-flight).
        const t = videoRef.current?.currentTime;
        if (t == null || !isFinite(t)) return;
        if (drag.mode === "in") {
          nextStart = Math.max(0, Math.min(t, drag.origin_end - 0.3));
          nextEnd = drag.origin_end;
          if (Math.abs(nextStart - drag.origin_start) < 0.05) return;
        } else {
          nextEnd = Math.max(drag.origin_start + 0.3, Math.min(t, duration));
          nextStart = drag.origin_start;
          if (Math.abs(nextEnd - drag.origin_end) < 0.05) return;
        }
      } else if (!moved) {
        return;
      }
      try {
        await api.put(`/cuts/${drag.cut_id}`, {
          start_time: Number(nextStart.toFixed(2)),
          end_time: Number(nextEnd.toFixed(2)),
        });
        pushUndo({
          type: "trim",
          cut_id: drag.cut_id,
          before: { start_time: drag.origin_start, end_time: drag.origin_end },
          after:  { start_time: nextStart, end_time: nextEnd },
          was_clipped: drag.is_clipped,
        });
        if (drag.is_clipped) {
          // Delete the stale rendered clip and re-generate at the new bounds
          const oldClipId = clipsByCutId.get(drag.cut_id)?.id;
          if (oldClipId) {
            try { await api.delete(`/clips/${oldClipId}`); } catch {}
          }
          toast.success("Cut trimmed — re-rendering clip…");
          refreshCuts();
          await handleGenerate([drag.cut_id]);
        } else {
          toast.success(`Cut updated · ${fmtDuration(nextEnd - nextStart)}`);
          refreshCuts();
        }
      } catch (err) {
        toast.error("Save failed: " + (err?.response?.data?.detail || err.message));
        refreshCuts();
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragCut, liveBounds, duration]);

  const onTimelineClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    clearStopAt();
    seek(ratio * duration);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-0 min-h-[calc(100vh-0px)] md:min-h-screen" data-testid="editor-page">
      {/* Main / Player */}
      <div className="flex flex-col bg-stone-950 border-r border-stone-800">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-800">
          <div className="min-w-0 flex-1">
            <div className="label-tiny">Editing</div>
            <div className="flex items-center gap-2 min-w-0">
              <div className="font-display uppercase text-xl truncate" data-testid="editor-video-title">{video.original_name}</div>
              <RenamePencilButton
                onClick={() => setShowVideoRename(true)}
                title="Rename this video (updates everywhere it appears)"
                testId="editor-video-rename-btn"
                className="shrink-0"
              />
            </div>
            {(video.event_name || video.event_date || video.association) && (
              <div className="text-xs text-orange-400 font-mono-tc truncate">
                {video.association ? `${video.association} · ` : ""}{video.event_name || ""}{video.event_name && video.event_date ? " · " : ""}{video.event_date || ""}
              </div>
            )}
          </div>
          <button
            onClick={() => setShowPublishMaster(true)}
            className="btn-secondary mr-2 inline-flex items-center gap-1.5 hover:!border-amber-500 hover:!text-amber-400"
            title="Publish this full source video as a Master Recording under an Association / Performance / Gallery"
            data-testid="editor-publish-master-btn"
          >
            <Video size={12} /> Publish Master
          </button>
          <button
            onClick={() => setShowSendAllClips(true)}
            disabled={clipsByCutId.size === 0}
            className="btn-secondary mr-2 inline-flex items-center gap-1.5 hover:!border-sky-500 hover:!text-sky-400 disabled:opacity-40 disabled:cursor-not-allowed"
            title={clipsByCutId.size === 0
              ? "Generate at least one clip first"
              : `Assign every rendered clip in this session (${clipsByCutId.size}) to an Association / Performance / Gallery at once`}
            data-testid="editor-send-all-clips-btn"
          >
            <FolderHeart size={12} /> Send All Clips
            {clipsByCutId.size > 0 && (
              <span className="text-[9px] font-mono-tc text-sky-400 ml-1">({clipsByCutId.size})</span>
            )}
          </button>
          <button
            onClick={() => setShowAddVideo(true)}
            className="btn-secondary mr-2 inline-flex items-center gap-1.5 hover:!border-orange-700 hover:!text-orange-400"
            title="Fetch a new video by URL without leaving this page"
            data-testid="editor-add-video-btn"
          >
            <Plus size={12} /> Add Video
          </button>
          <button
            onClick={() => setShowEventEdit(true)}
            className="btn-secondary mr-2 inline-flex items-center gap-1.5"
            title="Edit performance name/date"
            data-testid="edit-event-btn"
          >
            <Pencil size={12} /> Performance
          </button>
          <button
            onClick={undo}
            disabled={undoStack.length === 0}
            className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
            title="Undo (Ctrl+Z)"
            data-testid="undo-btn"
          >
            <Undo2 size={12} className="inline mr-1" /> Undo
            {undoStack.length > 0 && <span className="ml-1 text-[9px] text-orange-400 font-mono-tc">({undoStack.length})</span>}
          </button>
          <button
            onClick={redo}
            disabled={redoStack.length === 0}
            className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
            title="Redo (Ctrl+Shift+Z)"
            data-testid="redo-btn"
          >
            <Redo2 size={12} className="inline mr-1" /> Redo
          </button>
          <button
            onClick={() => setShowOverlayModal(true)}
            className="btn-secondary"
            data-testid="remove-overlay-btn"
            title="Remove a watermark, crosshair, or any static overlay from every clip generated from this video"
          >
            <Eraser size={12} className="inline mr-1" /> Remove Overlay
            {(video.overlay_removals || []).length > 0 && (
              <span className="ml-1 text-[9px] text-orange-400 font-mono-tc">
                ({(video.overlay_removals || []).length})
              </span>
            )}
          </button>
          <button
            onClick={() => navigate("/studio")}
            className="btn-secondary"
            data-testid="back-to-studio"
          >
            <ChevronLeft size={14} className="inline mr-1" /> Back
          </button>
          <button
            onClick={() => setShowHelp(true)}
            className="btn-secondary ml-2 inline-flex items-center gap-1.5"
            title="How to make good cuts"
            data-testid="show-help-btn"
          >
            <HelpCircle size={14} /> Help
          </button>
        </div>

        {showHelp && (
          <HelpPanel onClose={() => {
            setShowHelp(false);
            localStorage.setItem("rcs_help_dismissed", "1");
          }} />
        )}

        {showEventEdit && (
          <EventEditModal
            video={video}
            onClose={() => setShowEventEdit(false)}
            onSaved={() => { setShowEventEdit(false); window.location.reload(); }}
          />
        )}

        {showVideoRename && (
          <RenameModal
            label="Rename Video"
            currentName={video.original_name}
            subtitle="Updates this video's title everywhere it appears — cuts and rendered clips inherit the new name."
            testIdPrefix="editor-video-rename"
            onSave={async (newName) => {
              const r = await api.put(`/videos/${video.id}/rename`, { new_name: newName });
              const bits = [];
              if (r.data?.clips_renamed) bits.push(`${r.data.clips_renamed} clips`);
              if (r.data?.cuts_renamed) bits.push(`${r.data.cuts_renamed} cuts`);
              toast.success(`Renamed${bits.length ? " · " + bits.join(", ") : ""}`);
              refreshVideo && refreshVideo();
            }}
            onClose={() => setShowVideoRename(false)}
          />
        )}

        {renamingCut && (
          <RenameModal
            label="Rename Cut"
            currentName={renamingCut.sub_label || ""}
            subtitle="Updates this cut's sub-label — the Library and rendered clip inherit the change."
            testIdPrefix="editor-cut-rename"
            maxLen={120}
            onSave={async (newName) => {
              await api.put(`/cuts/${renamingCut.id}`, { sub_label: newName });
              toast.success("Cut renamed");
              refreshCuts && refreshCuts();
            }}
            onClose={() => setRenamingCut(null)}
          />
        )}

        {showTrackRider && (
          <TrackRiderModal
            video={video}
            currentTime={current}
            videoSrc={`${BACKEND_URL}/api/videos/${video.id}/stream`}
            onClose={() => setShowTrackRider(false)}
            onDone={(jobId) => { setShowTrackRider(false); refreshCuts(); toast.success("Tracking complete — new cuts added"); }}
          />
        )}

        {showOverlayModal && (
          <RemoveOverlayModal
            video={video}
            currentTime={current}
            videoSrc={`${BACKEND_URL}/api/videos/${video.id}/stream`}
            onClose={() => setShowOverlayModal(false)}
            onSaved={() => { setShowOverlayModal(false); window.location.reload(); }}
          />
        )}

        {showAddVideo && (
          <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowAddVideo(false)} data-testid="add-video-modal">
            <div className="bg-stone-950 border border-stone-700 max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-stone-800">
                <div className="font-display uppercase text-xl flex items-center gap-2">
                  <Plus size={16} className="text-orange-500" /> Add Video
                </div>
                <button onClick={() => setShowAddVideo(false)} className="btn-secondary" data-testid="add-video-close"><X size={14} /></button>
              </div>
              <div className="p-5">
                <p className="text-stone-400 text-sm mb-5">
                  Paste a Vimeo, YouTube, or direct video URL. Once it's ready, you'll jump straight into the editor for that new video.
                </p>
                <UrlFetchPanel
                  onComplete={(id) => {
                    setShowAddVideo(false);
                    navigate(`/studio/${id}`);
                  }}
                />
                <div className="mt-5 pt-5 border-t border-stone-800 text-center">
                  <button
                    onClick={() => { setShowAddVideo(false); navigate("/studio"); }}
                    className="text-xs uppercase tracking-widest text-stone-500 hover:text-orange-400"
                    data-testid="add-video-go-upload"
                  >
                    Or upload a file from your computer →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showPublishMaster && (
          <PublishMasterModal
            video={video}
            onClose={() => setShowPublishMaster(false)}
            onPublished={() => setShowPublishMaster(false)}
          />
        )}

        {showSendAllClips && (
          <SendAllClipsModal
            video={video}
            clips={Array.from(clipsByCutId.values())}
            onClose={() => setShowSendAllClips(false)}
            onSaved={() => { setShowSendAllClips(false); refreshGenerated(); }}
          />
        )}

        {/* Player */}
        <div className="bg-black relative">
          {videoError && (
            <div className="absolute inset-x-0 top-0 z-20 bg-amber-950/90 border-b border-amber-700 px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="video-error-banner">
              <div className="flex-1 min-w-0">
                <div className="text-amber-300 text-sm font-medium">Video can't play in the browser</div>
                <div className="text-amber-400/80 text-xs mt-0.5">
                  Format not supported by HTML5 (probably MPEG-1/2, WMV, MKV or similar). Convert to MP4 to unlock playback + clipping.
                </div>
              </div>
              <button
                onClick={async () => {
                  try {
                    await api.post(`/videos/${video.id}/compress`);
                    toast.info("Converting to MP4… this can take a few minutes. Refresh when done.");
                    setVideoError(false);
                  } catch (err) {
                    toast.error("Failed to start conversion: " + (err?.response?.data?.detail || err.message));
                  }
                }}
                className="btn-primary !py-1.5 !px-3 text-[11px]"
                data-testid="convert-video-btn"
              >
                Convert to MP4
              </button>
            </div>
          )}
          <video
            ref={videoRef}
            src={`${BACKEND_URL}/api/videos/${video.id}/stream`}
            className="w-full max-h-[60vh] mx-auto block"
            onLoadedMetadata={(e) => {
              setDuration(e.currentTarget.duration);
              setVideoError(false);
              // Consume ?seek=&in=&out= query params — used when opening a
              // Library clip in Studio. Only fires once so the user can
              // freely seek later without getting yanked back.
              if (!pendingSeekAppliedRef.current) {
                const qs = searchParams.get("seek");
                const qin = searchParams.get("in");
                const qout = searchParams.get("out");
                if (qs != null) {
                  const t = parseFloat(qs);
                  if (!isNaN(t) && t >= 0) {
                    try { e.currentTarget.currentTime = t; } catch {}
                  }
                }
                if (qin != null) {
                  const t = parseFloat(qin);
                  if (!isNaN(t) && t >= 0) setMarkIn(Number(t.toFixed(2)));
                }
                if (qout != null) {
                  const t = parseFloat(qout);
                  if (!isNaN(t) && t > 0) setMarkOut(Number(t.toFixed(2)));
                }
                if (qs != null || qin != null || qout != null) {
                  // Clear the params so a manual reload doesn't re-seek.
                  const next = new URLSearchParams(searchParams);
                  next.delete("seek"); next.delete("in"); next.delete("out");
                  setSearchParams(next, { replace: true });
                  toast.info("Studio positioned at Library clip range");
                }
                pendingSeekAppliedRef.current = true;
              }
            }}
            onTimeUpdate={(e) => {
              const t = e.currentTarget.currentTime;
              setCurrent(t);
              if (stopAtRef.current != null && t >= stopAtRef.current) {
                e.currentTarget.pause();
                stopAtRef.current = null;
              }
            }}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onError={() => setVideoError(true)}
            controls={false}
            data-testid="video-player"
          />
        </div>

        {/* Transport */}
        <div className="px-4 py-4 border-b border-stone-800">
          <div className="flex items-center gap-3">
            <button onClick={onPlayPause} className="btn-secondary !p-3" data-testid="play-pause-btn">
              {playing ? <Pause size={16} /> : <Play size={16} />}
            </button>
            <button onClick={() => seek(current - 5)} className="btn-secondary !p-3" data-testid="seek-back-btn">
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => seek(current + 5)} className="btn-secondary !p-3" data-testid="seek-fwd-btn">
              <ChevronRight size={16} />
            </button>
            <div className="font-mono-tc text-orange-500 text-lg ml-3" data-testid="current-time">
              {fmtTime(current)}
            </div>
            <div className="text-stone-600 font-mono-tc">/</div>
            <div className="font-mono-tc text-stone-400" data-testid="total-duration">
              {fmtTime(duration)}
            </div>
            <div className="ml-auto flex items-center gap-1.5" data-testid="speed-controls">
              <span className="label-tiny mr-1">Speed</span>
              {[0.25, 0.5, 1, 1.5, 2].map((r) => (
                <button
                  key={r}
                  onClick={() => setPlaybackRate(r)}
                  className={`px-2 py-1 text-[11px] font-mono-tc border transition-colors ${playbackRate === r ? "border-orange-500 text-orange-500 bg-orange-600/10" : "border-stone-800 text-stone-400 hover:border-stone-700 hover:text-stone-200"}`}
                  data-testid={`speed-${r}`}
                  title={r < 1 ? "Slow motion" : r > 1 ? "Fast forward" : "Normal speed"}
                >
                  {r}x
                </button>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="flex items-center gap-2 mt-4 mb-1">
            <span className="label-tiny">Zoom</span>
            <button onClick={() => setZoom((z) => Math.max(1, +(z / 1.5).toFixed(2)))} className="btn-secondary !p-1.5" data-testid="zoom-out-btn">
              <ZoomOut size={12} />
            </button>
            <span className="font-mono-tc text-xs text-stone-400 w-12 text-center">{zoom.toFixed(1)}x</span>
            <button onClick={() => setZoom((z) => Math.min(25, +(z * 1.5).toFixed(2)))} className="btn-secondary !p-1.5" data-testid="zoom-in-btn">
              <ZoomIn size={12} />
            </button>
            {zoom > 1 && (
              <button onClick={() => setZoom(1)} className="text-xs text-stone-500 hover:text-orange-500 ml-1" data-testid="zoom-reset">reset</button>
            )}
            {/* Event color legend — shows only categories present in current cuts.
                Gives admins a quick visual map of which events the AI/manual
                cuts have detected on this video's timeline. */}
            <div className="ml-auto flex flex-wrap items-center gap-2" data-testid="timeline-color-legend">
              {(() => {
                const seen = new Set();
                const order = [];
                cuts.forEach((c) => {
                  if (c.is_dead_spot) return;
                  const cat = c.category || "unknown";
                  if (!seen.has(cat)) { seen.add(cat); order.push(cat); }
                });
                const hasDead = cuts.some((c) => c.is_dead_spot);
                return (
                  <>
                    {order.map((cat) => {
                      const sw = categoryColor(cat);
                      return (
                        <span key={cat} className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest ${sw.text}`}>
                          <span className={`inline-block w-3 h-3 ${sw.fill} border ${sw.ring}`} />
                          {CATEGORY_NAME[cat] || cat}
                        </span>
                      );
                    })}
                    {hasDead && (
                      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-stone-400">
                        <span className={`inline-block w-3 h-3 ${DEAD_SPOT_COLOR.fill} border-dashed border ${DEAD_SPOT_COLOR.ring}`} />
                        Dead spot
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
          <div className="overflow-x-auto pt-6 relative" data-testid="timeline-scroll" ref={timelineScrollRef}>
          <div
            ref={timelineRef}
            className="relative h-12 bg-stone-900 cursor-pointer border border-stone-800"
            style={{ width: `${zoom * 100}%`, minWidth: "100%" }}
            onClick={onTimelineClick}
            data-testid="timeline"
          >
            {/* cut regions */}
            {cuts.map((c) => {
              const live = liveBounds && liveBounds.cut_id === c.id ? liveBounds : null;
              const s = live ? live.start : c.start_time;
              const e = live ? live.end   : c.end_time;
              const left = duration > 0 ? (s / duration) * 100 : 0;
              const width = duration > 0 ? ((e - s) / duration) * 100 : 0;
              const isClipped = clipsByCutId.has(c.id);
              const isDragging = dragCut && dragCut.cut_id === c.id;
              // Color the segment by its event/category. Dead-spot cuts get
              // a muted gray so admins can see them but they visually recede.
              // Clipping status is layered on as a subtle emerald stripe — the
              // event color always wins so admins can eyeball event boundaries.
              const swatch = c.is_dead_spot ? DEAD_SPOT_COLOR : categoryColor(c.category);
              const bodyBg = `${swatch.fill} border-y ${swatch.ring}/70`;
              return (
                <div
                  key={c.id}
                  className={`absolute top-0 h-full group/cut ${bodyBg} ${isDragging ? "ring-1 ring-white z-10" : ""} ${c.is_dead_spot ? "opacity-70" : ""}`}
                  style={{ left: `${left}%`, width: `${Math.max(0.3, width)}%` }}
                  data-testid={`timeline-cut-${c.id}`}
                  title={`${c.is_dead_spot ? "DEAD SPOT · " : ""}${c.sub_label || CATEGORY_NAME[c.category] || c.category} (${fmtTime(s)}–${fmtTime(e)})${isClipped ? " · clipped" : ""} · right-click for options`}
                  onContextMenu={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    // Clamp to viewport — menu is ~250px tall
                    const menuH = 220;
                    const menuW = 220;
                    const vw = window.innerWidth;
                    const vh = window.innerHeight;
                    const x = Math.min(ev.clientX, vw - menuW - 8);
                    const y = ev.clientY + menuH > vh ? ev.clientY - menuH : ev.clientY;
                    setCutMenu({ cut_id: c.id, x, y });
                  }}
                >
                  {/* IN handle (left edge) — drag to trim, or single-click to snap to playhead */}
                  <div
                    onPointerDown={onCutPointerDown(c, "in")}
                    className="absolute top-0 left-0 h-full w-2 -ml-0.5 bg-white/20 hover:bg-sky-400 cursor-ew-resize z-10 border-l border-white/60 hover:border-sky-200 transition-colors"
                    title="Drag to trim · click to snap IN to current playhead"
                    data-testid={`timeline-cut-in-${c.id}`}
                  />
                  {/* OUT handle (right edge) — drag to trim, or single-click to snap to playhead */}
                  <div
                    onPointerDown={onCutPointerDown(c, "out")}
                    className="absolute top-0 right-0 h-full w-2 -mr-0.5 bg-white/20 hover:bg-sky-400 cursor-ew-resize z-10 border-r border-white/60 hover:border-sky-200 transition-colors"
                    title="Drag to trim · click to snap OUT to current playhead"
                    data-testid={`timeline-cut-out-${c.id}`}
                  />
                  {/* Body (slide) */}
                  <div
                    onPointerDown={onCutPointerDown(c, "move")}
                    className="absolute inset-0 mx-1.5 cursor-grab active:cursor-grabbing"
                    data-testid={`timeline-cut-body-${c.id}`}
                  />
                  {/* Clipped indicator — emerald stripe at the top so admins can
                      tell at-a-glance which cuts already have an mp4 without
                      losing the event-color context. */}
                  {isClipped && (
                    <div className="absolute top-0 left-0 right-0 h-1 bg-emerald-500 pointer-events-none" />
                  )}
                  {/* Tag while dragging */}
                  {isDragging && (
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-orange-500 text-black text-[10px] font-mono-tc font-bold whitespace-nowrap z-20 pointer-events-none">
                      {fmtTime(s)} → {fmtTime(e)} · {fmtDuration(e - s)}
                    </div>
                  )}
                </div>
              );
            })}
            {/* mark in */}
            {markIn != null && (
              <div
                className="absolute top-0 h-full w-0.5 bg-emerald-500 z-20"
                style={{ left: `${(markIn / duration) * 100}%` }}
                data-testid="mark-in-line"
              >
                <div className="absolute -top-2 -left-3 px-1 text-[9px] bg-emerald-500 text-black font-bold uppercase">In</div>
              </div>
            )}
            {markOut != null && (
              <div
                className="absolute top-0 h-full w-0.5 bg-rose-500 z-20"
                style={{ left: `${(markOut / duration) * 100}%` }}
                data-testid="mark-out-line"
              >
                <div className="absolute -top-2 -left-3 px-1 text-[9px] bg-rose-500 text-white font-bold uppercase">Out</div>
              </div>
            )}
            {/* playhead */}
            <div
              className="absolute top-0 w-0.5 h-full bg-orange-500 z-30 pulse-orange"
              style={{ left: `${(current / Math.max(0.001, duration)) * 100}%` }}
              data-testid="playhead"
            />
          </div>
          {/* Cut right-click context menu */}
          {cutMenu && (() => {
            const c = cuts.find((x) => x.id === cutMenu.cut_id);
            if (!c) return null;
            const isClipped = clipsByCutId.has(c.id);
            return (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setCutMenu(null)}
                  onContextMenu={(e) => { e.preventDefault(); setCutMenu(null); }}
                />
                <div
                  className="fixed z-50 bg-stone-950 border border-orange-700 shadow-xl text-sm min-w-[220px]"
                  style={{ left: cutMenu.x, top: cutMenu.y }}
                  data-testid="cut-context-menu"
                >
                  <div className="px-3 py-2 border-b border-stone-800 text-[10px] uppercase tracking-widest text-stone-500 font-mono-tc truncate">
                    {c.sub_label || c.category} · {fmtDuration(c.end_time - c.start_time)}
                  </div>
                  <button
                    onClick={() => { seek(c.start_time, true); setCutMenu(null); }}
                    className="block w-full text-left px-3 py-2 hover:bg-stone-900 text-stone-200"
                    data-testid="ctx-play-cut"
                  >
                    ▶ Play this cut
                  </button>
                  <button
                    onClick={() => { seek(c.start_time, false); setCutMenu(null); }}
                    className="block w-full text-left px-3 py-2 hover:bg-stone-900 text-stone-200"
                    data-testid="ctx-jump-to-in"
                  >
                    ⤴ Jump to IN
                  </button>
                  {!isClipped && (
                    <button
                      onClick={() => { handleGenerate([c.id]); setCutMenu(null); }}
                      className="block w-full text-left px-3 py-2 hover:bg-stone-900 text-orange-400"
                      data-testid="ctx-generate"
                    >
                      ⚡ Generate clip
                    </button>
                  )}
                  <button
                    onClick={async () => {
                      const newVal = !c.is_dead_spot;
                      try {
                        await api.put(`/cuts/${c.id}`, { is_dead_spot: newVal });
                        if (typeof refreshCuts === "function") refreshCuts();
                        toast.success(newVal ? "Marked as dead spot" : "Unmarked");
                      } catch {
                        toast.error("Failed to update");
                      }
                      setCutMenu(null);
                    }}
                    className={`block w-full text-left px-3 py-2 hover:bg-stone-900 border-t border-stone-800 ${c.is_dead_spot ? "text-emerald-400" : "text-stone-400"}`}
                    data-testid="ctx-toggle-dead-spot"
                  >
                    {c.is_dead_spot ? "↺ Unmark dead spot (restore)" : "⊘ Mark as dead spot"}
                  </button>
                  <button
                    onClick={async () => {
                      const oldClipId = clipsByCutId.get(c.id)?.id;
                      if (isClipped && oldClipId) {
                        try { await api.delete(`/clips/${oldClipId}`); } catch {}
                      }
                      await handleDeleteCut(c);
                      setCutMenu(null);
                    }}
                    className="block w-full text-left px-3 py-2 hover:bg-rose-900/30 text-rose-400 border-t border-stone-800"
                    data-testid="ctx-delete"
                  >
                    🗑 Delete cut{isClipped ? " + clip" : ""}
                  </button>
                </div>
              </>
            );
          })()}
          </div>

          {/* Mark buttons */}
          <div className="flex flex-wrap gap-2 mt-4">
            <button
              onClick={() => setMarkIn(current)}
              className="btn-secondary"
              title="Set IN point at playhead (shortcut: I)"
              data-testid="set-mark-in"
            >
              <span className="text-emerald-500 font-bold">▌</span> Set IN <kbd className="ml-1 px-1 text-[9px] bg-stone-800 border border-stone-700 text-stone-400">I</kbd> ({fmtTime(markIn || 0)})
            </button>
            <button
              onClick={() => setMarkOut(current)}
              className="btn-secondary"
              title="Set OUT point at playhead (shortcut: O)"
              data-testid="set-mark-out"
            >
              <span className="text-rose-500 font-bold">▌</span> Set OUT <kbd className="ml-1 px-1 text-[9px] bg-stone-800 border border-stone-700 text-stone-400">O</kbd> ({fmtTime(markOut || 0)})
            </button>
            <button
              onClick={queueRange}
              disabled={markIn == null || markOut == null}
              className="btn-secondary inline-flex items-center gap-1.5 hover:!border-emerald-700 hover:!text-emerald-400 disabled:opacity-40"
              title="Queue the current IN/OUT as a pending cut so you can mark more before saving (shortcut: Q)"
              data-testid="queue-range-btn"
            >
              <Plus size={13} /> Queue <kbd className="px-1 text-[9px] bg-stone-800 border border-stone-700 text-stone-400">Q</kbd>
            </button>
            <button
              onClick={() => { setMarkIn(null); setMarkOut(null); }}
              className="btn-secondary"
              data-testid="clear-marks"
            >
              <X size={14} className="inline" /> Clear
            </button>
            <div className="flex-1" />
            <button
              onClick={() => setShowTrackRider(true)}
              className="btn-secondary inline-flex items-center gap-2 hover:!border-orange-700 hover:!text-orange-400"
              title="Pause on a frame, draw a box around a rider, AI finds every appearance"
              data-testid="track-rider-btn"
            >
              <Crosshair size={14} /> Track Rider
            </button>
            <button
              onClick={handleAISuggest}
              disabled={aiBusy}
              className="btn-ai inline-flex items-center gap-2"
              data-testid="ai-suggest-btn"
            >
              {aiBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {aiBusy ? "Gemini analyzing…" : "AI Suggest Cuts"}
            </button>
            <button
              onClick={() => handleDeepScan("runs")}
              disabled={aiBusy}
              className="btn-secondary inline-flex items-center gap-2 hover:!border-orange-700 hover:!text-orange-400"
              title="Splits the video into 10-min windows and runs Gemini on each — much more thorough than AI Suggest, especially for long reels with spread-out runs"
              data-testid="ai-deep-scan-btn"
            >
              <Wand2 size={14} /> Deep Scan
            </button>
            <select
              onChange={(e) => {
                const v = e.target.value;
                e.target.value = "";  // reset so re-picking same option fires again
                if (v) handleDeepScan(v);
              }}
              disabled={aiBusy}
              defaultValue=""
              className="bg-stone-900 border border-stone-700 hover:border-orange-700 text-stone-300 hover:text-orange-400 text-[11px] uppercase tracking-widest font-bold px-2 py-1.5 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              title="Scan the video with a different Gemini prompt — find bloopers, highlights, reactions, etc."
              data-testid="scan-preset-picker"
            >
              <option value="" disabled>Scan for…</option>
              <option value="bloopers">Bloopers &amp; buck-offs</option>
              <option value="best_runs">Best runs only</option>
              <option value="reactions">Rider reactions</option>
              <option value="crowd_moments">Crowd moments</option>
            </select>
            <button
              onClick={handleIdentifyRiders}
              disabled={aiBusy}
              className="btn-secondary inline-flex items-center gap-2 hover:!border-orange-700 hover:!text-orange-400"
              title="Transcribe the announcer audio with Whisper, then auto-fill rider names on cuts that are blank"
              data-testid="identify-riders-btn"
            >
              <Mic size={14} /> Identify Riders
            </button>
            <button
              onClick={async () => {
                try {
                  const r = await api.post(`/videos/${video.id}/fill-dead-spots`);
                  if (r.data.inserted > 0) {
                    toast.success(`Marked ${r.data.inserted} dead spot${r.data.inserted === 1 ? "" : "s"}`);
                    if (typeof refreshCuts === "function") refreshCuts();
                  } else {
                    toast.info("No new dead spots — the timeline is already covered");
                  }
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Failed to fill dead spots");
                }
              }}
              className="btn-secondary inline-flex items-center gap-2 hover:!border-stone-500 hover:!text-stone-200"
              title="Fill every ≥5s gap between live cuts with a dead-spot marker (safe to re-run — idempotent)"
              data-testid="fill-dead-spots-btn"
            >
              ⊘ Fill Dead Spots
            </button>
          </div>

          {aiProgress && (
            <div className="mt-3 border border-orange-700/40 bg-stone-900/50 px-4 py-3" data-testid="ai-suggest-progress">
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="text-orange-400 uppercase tracking-widest font-bold inline-flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" />
                  AI Suggest · {AI_STAGE_INFO[aiProgress.stage]?.label || aiProgress.stage || "Working"}
                </span>
                <span className="font-mono-tc text-stone-300">
                  {Math.floor(aiProgress.elapsed / 60)}m {String(aiProgress.elapsed % 60).padStart(2, "0")}s elapsed
                </span>
              </div>
              <div className="text-[11px] text-stone-400 leading-relaxed">
                {AI_STAGE_INFO[aiProgress.stage]?.hint || aiProgress.note || "Waiting for the worker to check in…"}
              </div>
              {aiProgress.streamedCount > 0 && (
                <div className="text-[11px] text-emerald-400 mt-1 font-mono-tc">
                  ✓ {aiProgress.streamedCount} cut{aiProgress.streamedCount === 1 ? "" : "s"} streamed so far — timeline updating live
                </div>
              )}
              {aiProgress.canCancel && (
                <div className="flex justify-end mt-2">
                  <button
                    onClick={cancelAiJob}
                    className="text-[10px] uppercase tracking-widest text-rose-400 hover:text-rose-300 border border-rose-800 hover:border-rose-600 px-2 py-1 transition-colors"
                    data-testid="ai-suggest-cancel"
                  >
                    ✕ Cancel job
                  </button>
                </div>
              )}
            </div>
          )}

          {resumeInfo && !deepScanProgress && !aiBusy && (
            <div className="mt-3 border border-amber-700/50 bg-amber-950/20 px-4 py-3 flex items-center justify-between gap-3"
                 data-testid="deep-scan-resume-banner">
              <div className="text-xs text-amber-200">
                <div className="uppercase tracking-widest font-bold text-amber-400 mb-1">
                  Deep Scan interrupted
                </div>
                <div className="text-stone-300">
                  Previous scan completed <span className="font-mono-tc text-amber-300">{resumeInfo.completed_windows}/{resumeInfo.total_windows}</span> windows
                  before the worker died. Resume from where it stopped — no wasted Gemini credits.
                </div>
              </div>
              <button
                type="button"
                onClick={handleResumeDeepScan}
                className="px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold text-amber-200 hover:text-white bg-amber-900/40 hover:bg-amber-800/60 border border-amber-700/70 hover:border-amber-500 transition-colors whitespace-nowrap"
                data-testid="deep-scan-resume-btn"
              >
                Resume Deep Scan
              </button>
            </div>
          )}

          {deepScanProgress && (
            <div className="mt-3 border border-orange-700/40 bg-stone-900/50 px-4 py-3" data-testid="deep-scan-progress">
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="text-orange-400 uppercase tracking-widest font-bold">
                  Deep Scan · window {deepScanProgress.completed}/{deepScanProgress.total || "?"}
                </span>
                <div className="flex items-center gap-3">
                  <span className="font-mono-tc text-stone-300">{deepScanProgress.pct}%</span>
                  {deepScanProgress.jobId && (
                    <button
                      type="button"
                      onClick={handleCancelDeepScan}
                      className="px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold text-red-400 hover:text-red-300 hover:bg-red-950/40 border border-red-800/50 hover:border-red-600 transition-colors"
                      data-testid="deep-scan-cancel-btn"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
              <div className="h-1.5 bg-stone-900 overflow-hidden">
                <div className="h-full bg-orange-500 transition-all" style={{ width: `${deepScanProgress.pct}%` }} />
              </div>
              <div className="text-[10px] font-mono-tc text-stone-500 mt-2">
                {deepScanProgress.found} new cut{deepScanProgress.found !== 1 ? "s" : ""} found so far
              </div>
            </div>
          )}

          {audioProgress && (
            <div className="mt-3 border border-orange-700/40 bg-stone-900/50 px-4 py-3" data-testid="audio-progress">
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="text-orange-400 uppercase tracking-widest font-bold">
                  Identify Riders · {audioProgress.stage === "downloading" && "Downloading"}
                  {audioProgress.stage === "extracting_audio" && "Extracting audio"}
                  {audioProgress.stage === "splitting" && "Preparing chunks"}
                  {audioProgress.stage === "transcribing" && "Whisper transcribing"}
                  {audioProgress.stage === "matching" && "Matching to cuts"}
                  {audioProgress.stage === "parsing" && "Gemini extracting names"}
                  {(!audioProgress.stage || audioProgress.stage === "starting" || audioProgress.stage === "queued") && "Starting…"}
                </span>
                <span className="font-mono-tc text-stone-300">{audioProgress.pct}%</span>
              </div>
              <div className="h-1.5 bg-stone-900 overflow-hidden">
                <div className="h-full bg-orange-500 transition-all" style={{ width: `${audioProgress.pct}%` }} />
              </div>
              <div className="text-[10px] font-mono-tc text-stone-500 mt-2">
                {audioProgress.matched} riders matched · {audioProgress.scanned} cuts scanned
              </div>
            </div>
          )}
        </div>

        {/* Pending queue — batch of IN/OUT ranges awaiting persistence */}
        {pendingRanges.length > 0 && (
          <div className="px-4 py-3 border-b border-stone-800 bg-emerald-950/20" data-testid="pending-queue">
            <div className="flex items-center justify-between mb-2">
              <div className="label-tiny text-emerald-400">
                Queued Ranges ({pendingRanges.length})
              </div>
              <button
                onClick={() => { setPendingRanges([]); toast.info("Queue cleared"); }}
                className="text-[10px] uppercase tracking-wider text-stone-500 hover:text-rose-400 inline-flex items-center gap-1"
                data-testid="clear-queue-btn"
              >
                <X size={11} /> Clear all
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {pendingRanges.map((r, i) => (
                <div
                  key={r.id}
                  className="inline-flex items-center gap-1.5 bg-stone-950 border border-emerald-700/60 px-2 py-1 text-xs font-mono-tc"
                  data-testid={`pending-chip-${i}`}
                >
                  <span className="text-emerald-500 font-bold">▌</span>
                  <span className="text-stone-300">{fmtTime(r.start)} → {fmtTime(r.end)}</span>
                  <span className="text-orange-400">· {fmtDuration(r.end - r.start)}</span>
                  <button
                    onClick={() => seek(r.start, false, r.end)}
                    className="text-stone-500 hover:text-orange-400 ml-1"
                    title="Preview this range"
                  >
                    ▶
                  </button>
                  <button
                    onClick={() => removePendingRange(r.id)}
                    className="text-stone-500 hover:text-rose-400"
                    title="Remove from queue"
                    data-testid={`pending-chip-remove-${i}`}
                  >
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cut Form */}
        <div className="px-4 py-4 border-b border-stone-800">
          <div className="label-tiny mb-3">
            {pendingRanges.length > 0
              ? `Metadata (applied to all ${pendingRanges.length + (markIn != null && markOut != null ? 1 : 0)} cuts)`
              : "New Cut Metadata"}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Association</div>
              <AutocompleteInput
                field="association"
                sourceEndpoint="/associations"
                sourceMap={(d) => (d.associations || []).map((a) => a.name)}
                value={formAssociation}
                onChange={setFormAssociation}
                placeholder="Pick an association — e.g. CCA, WPRA"
                className="field-input"
                data-testid="form-association"
              />
            </div>
            <div className="md:col-span-2">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Performance</div>
              <AutocompleteInput
                field="event_name"
                sourceEndpoint="/events"
                sourceMap={(d) => (d.events || []).map((e) => e.name)}
                value={formEventName}
                onChange={setFormEventName}
                placeholder="Pick a performance — e.g. Olds Open Jackpot · Round 1"
                className="field-input"
                data-testid="form-event-name"
              />
            </div>
            <div className="md:col-span-2">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Round / Session <span className="text-stone-600 normal-case tracking-normal">— optional slice within the performance</span></div>
              <AutocompleteInput
                field="performance"
                value={formPerformance}
                onChange={setFormPerformance}
                placeholder="e.g. Slack · Round 1 · Short Go"
                className="field-input"
                data-testid="form-performance"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Category</div>
              <select
                value={formCategory}
                onChange={(e) => setFormCategory(e.target.value)}
                className="field-input"
                data-testid="form-category"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Sub-label</div>
              <AutocompleteInput
                field="sub_label"
                value={formSubLabel}
                onChange={setFormSubLabel}
                placeholder="e.g. clean run, no-time"
                className="field-input"
                data-testid="form-sublabel"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Rider / Header</div>
              <AutocompleteInput
                field="rider"
                value={formRider}
                onChange={setFormRider}
                placeholder="e.g. Sarah Johnson"
                className="field-input"
                data-testid="form-rider"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Heeler <span className="text-stone-600 normal-case">(team roping)</span></div>
              <AutocompleteInput
                field="heeler"
                value={formHeeler}
                onChange={setFormHeeler}
                placeholder="e.g. Jake Miller"
                className="field-input"
                data-testid="form-heeler"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Horse</div>
              <AutocompleteInput
                field="horse"
                value={formHorse}
                onChange={setFormHorse}
                placeholder="e.g. Dusty"
                className="field-input"
                data-testid="form-horse"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Owner</div>
              <AutocompleteInput
                field="owner"
                value={formOwner}
                onChange={setFormOwner}
                placeholder="horse owner / breeder"
                className="field-input"
                data-testid="form-owner"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Notes</div>
              <AutocompleteInput
                field="notes"
                value={formNotes}
                onChange={setFormNotes}
                placeholder="optional"
                className="field-input"
                data-testid="form-notes"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Time <span className="normal-case text-stone-600">(seconds)</span></div>
              <input
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={formTime}
                onChange={(e) => setFormTime(e.target.value)}
                placeholder="e.g. 14.72"
                className="field-input"
                data-testid="form-time"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Score</div>
              <input
                type="number"
                step="0.5"
                inputMode="decimal"
                value={formScore}
                onChange={(e) => setFormScore(e.target.value)}
                placeholder="e.g. 87.5"
                className="field-input"
                data-testid="form-score"
              />
            </div>
          </div>
          {(() => {
            const currentCount = markIn != null && markOut != null ? 1 : 0;
            const total = pendingRanges.length + currentCount;
            const disabled = total === 0;
            const canSplice = markIn != null && markOut != null && markOut > markIn && !splicing;
            return (
              <>
                <button
                  onClick={handleAddCut}
                  disabled={disabled}
                  className="btn-primary w-full mt-4 flex items-center justify-center gap-2"
                  data-testid="add-cut-btn"
                >
                  <Scissors size={14} />
                  {total <= 1
                    ? "Add Cut to List"
                    : `Save ${total} Cuts to List`}
                </button>
                <button
                  onClick={handleSpliceRange}
                  disabled={!canSplice}
                  className="w-full mt-2 py-2 text-[11px] uppercase tracking-widest border border-rose-800/60 bg-rose-950/30 hover:bg-rose-900/40 text-rose-300 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                  title="Destructively remove the current IN→OUT range from the raw source video. Overlapping cuts are deleted; later cuts shift left."
                  data-testid="splice-range-btn"
                >
                  {splicing ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  {splicing ? "Splicing raw video…" : "Delete range from raw video"}
                </button>
              </>
            );
          })()}
        </div>
      </div>

      {/* Right side panel: Cut List */}
      <CutListPanel
        cuts={cuts}
        duration={duration}
        videoId={video.id}
        current={current}
        onSeek={seek}
        onDelete={handleDeleteCut}
        onFeedback={handleFeedback}
        onRefresh={refreshCuts}
        onGenerate={handleGenerate}
        generating={generating}
        generatingCutId={generatingCutId}
        clipsByCutId={clipsByCutId}
        pushUndo={pushUndo}
        onScanScoreboard={fireScoreboardScan}
        scoreboardBusy={scoreboardBusy}
        onRenameCut={(c) => setRenamingCut({ id: c.id, sub_label: c.sub_label || "" })}
      />
    </div>
  );
}

function CutListPanel({ cuts, duration, videoId, current, onSeek, onDelete, onFeedback, onRefresh, onGenerate, generating, generatingCutId, clipsByCutId, pushUndo, onScanScoreboard, scoreboardBusy, onRenameCut }) {
  // Dead spots clutter the cut list. Hide them by default; user can toggle on to
  // review / reclassify them.
  const [showDead, setShowDead] = useState(false);
  const deadCount = cuts.filter((c) => c.is_dead_spot).length;
  const visibleCuts = showDead ? cuts : cuts.filter((c) => !c.is_dead_spot);
  const liveCount = cuts.length - deadCount;
  const aiCount = cuts.filter((c) => c.ai_suggested && !c.accepted && !c.is_dead_spot).length;
  const [editingCutId, setEditingCutId] = useState(null);
  const [catEditingId, setCatEditingId] = useState(null);

  const changeCategory = async (cut, newCategory) => {
    if (!newCategory || newCategory === cut.category) {
      setCatEditingId(null);
      return;
    }
    try {
      await api.put(`/cuts/${cut.id}`, { category: newCategory });
      setCatEditingId(null);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error("Failed to update category");
    }
  };

  const saveCutTimes = async (cut, newStart, newEnd) => {
    const wasClipped = clipsByCutId.has(cut.id);
    try {
      await api.put(`/cuts/${cut.id}`, { start_time: newStart, end_time: newEnd });
      pushUndo && pushUndo({
        type: "trim",
        cut_id: cut.id,
        before: { start_time: cut.start_time, end_time: cut.end_time },
        after: { start_time: newStart, end_time: newEnd },
        was_clipped: wasClipped,
      });
      if (wasClipped) {
        // Find the rendered clip for this cut, delete the old .mp4 from S3,
        // then re-render the cut with the new bounds.
        const oldClipId = clipsByCutId.get(cut.id)?.id;
        if (oldClipId) {
          try { await api.delete(`/clips/${oldClipId}`); } catch { /* ignore */ }
        }
        toast.success("Cut trimmed — re-rendering clip…");
        setEditingCutId(null);
        onRefresh();
        // Kick a single-cut regenerate
        onGenerate([cut.id]);
      } else {
        toast.success(`Cut updated · ${fmtDuration(newEnd - newStart)}`);
        setEditingCutId(null);
        onRefresh();
      }
    } catch (err) {
      toast.error("Update failed: " + (err?.response?.data?.detail || err.message));
    }
  };
  return (
    <div className="bg-stone-900 border-t lg:border-t-0 border-stone-800 flex flex-col" data-testid="cut-list-panel">
      <div className="px-4 py-3 border-b border-stone-800 flex items-center justify-between gap-3">
        <div>
          <div className="label-tiny">Cut List</div>
          <div className="font-display uppercase text-2xl font-bold leading-none mt-1">
            {liveCount} <span className="text-orange-500">cuts</span>
            {deadCount > 0 && (
              <span className="text-stone-500 text-xs font-mono-tc font-normal ml-2">
                +{deadCount} dead
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {deadCount > 0 && (
            <button
              onClick={() => setShowDead((v) => !v)}
              className={`text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors ${showDead ? "border-stone-500 text-stone-200 bg-stone-800" : "border-stone-700 text-stone-500 hover:text-stone-300"}`}
              title="Toggle visibility of dead-spot markers in this list"
              data-testid="toggle-show-dead"
            >
              {showDead ? "Hide dead" : "Show dead"}
            </button>
          )}
          {aiCount > 0 && (
            <div className="text-amber-500 text-[10px] uppercase tracking-widest border border-amber-500/40 px-2 py-1">
              {aiCount} AI pending
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto max-h-[60vh] lg:max-h-[calc(100vh-220px)]">
        {visibleCuts.length === 0 ? (
          <div className="p-8 text-center text-stone-500 text-sm" data-testid="empty-cuts">
            {cuts.length === 0 ? "No cuts yet. Use IN/OUT markers or AI Suggest." : "No live cuts to show. Toggle 'Show dead' to see dead-spot markers."}
          </div>
        ) : (
          visibleCuts.map((c, idx) => {
            const isGenerated = clipsByCutId.has(c.id);
            const isGenerating = generatingCutId === c.id;
            // Highlight the cut whose time window contains the current playhead —
            // makes it obvious which cut is being previewed in the main video.
            const isPlaying = current != null && current >= c.start_time && current < c.end_time;
            return (
            <div
              key={c.id}
              onClick={(e) => {
                // Ignore clicks that originated inside interactive children (buttons,
                // inputs, kbd, links). We only want the "empty area" click to preview.
                const tag = (e.target?.tagName || "").toLowerCase();
                if (["button", "input", "textarea", "select", "svg", "path", "kbd", "a"].includes(tag)) return;
                if (e.target?.closest("button, input, textarea, select, a")) return;
                onSeek(c.start_time, true, c.end_time);
              }}
              className={`px-4 py-3 border-b border-stone-800 cursor-pointer transition-colors ${
                isPlaying
                  ? "bg-orange-950/40 border-l-4 border-l-orange-500 ring-1 ring-inset ring-orange-800/50"
                  : "hover:bg-stone-800/50 " + (c.ai_suggested && !c.accepted ? "border-l-2 border-l-amber-500" : "")
              }`}
              title="Click to preview this cut in the main player"
              data-testid={`cut-item-${c.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="font-mono-tc text-stone-500 text-xs">#{(idx + 1).toString().padStart(2, "0")}</div>
                <div className="flex gap-1">
                  <button
                    onClick={() => onFeedback(c, "good")}
                    className={`p-1 transition-colors ${c.feedback === "good" ? "text-emerald-400" : "text-stone-600 hover:text-emerald-400"}`}
                    title="Mark as good example"
                    data-testid={`thumbs-up-${c.id}`}
                  >
                    <ThumbsUp size={12} fill={c.feedback === "good" ? "currentColor" : "none"} />
                  </button>
                  <button
                    onClick={() => onFeedback(c, "bad")}
                    className={`p-1 transition-colors ${c.feedback === "bad" ? "text-rose-400" : "text-stone-600 hover:text-rose-400"}`}
                    title="Mark as bad example"
                    data-testid={`thumbs-down-${c.id}`}
                  >
                    <ThumbsDown size={12} fill={c.feedback === "bad" ? "currentColor" : "none"} />
                  </button>
                  <button
                    onClick={() => onSeek(c.start_time, true, c.end_time)}
                    className="text-stone-500 hover:text-orange-500 p-1"
                    title="Play this cut only"
                    data-testid={`seek-cut-${c.id}`}
                  >
                    <Play size={12} />
                  </button>
                  <button
                    onClick={() => onDelete(c)}
                    className="text-stone-500 hover:text-rose-500 p-1"
                    title="Delete"
                    data-testid={`delete-cut-${c.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-2 relative">
                <span className="tag-cat">{CATEGORY_NAME[c.category] || c.category}</span>
                <button
                  onClick={() => setCatEditingId(catEditingId === c.id ? null : c.id)}
                  className="text-stone-500 hover:text-orange-400 p-0.5 transition-colors"
                  title="Change category"
                  data-testid={`cut-edit-cat-${c.id}`}
                >
                  <Pencil size={10} />
                </button>
                {c.ai_suggested && (
                  <span className="text-[9px] uppercase tracking-wider text-amber-500 border border-amber-500/40 px-1.5 py-0.5 inline-flex items-center gap-1">
                    <Wand2 size={9} /> AI
                  </span>
                )}
                {isGenerated && (
                  <span className="text-[9px] uppercase tracking-wider text-emerald-400 border border-emerald-500/40 px-1.5 py-0.5 inline-flex items-center gap-1" data-testid={`cut-generated-${c.id}`}>
                    <Check size={9} /> Clipped
                  </span>
                )}
                {catEditingId === c.id && (
                  <>
                    <div className="fixed inset-0 z-20" onClick={() => setCatEditingId(null)} />
                    <div className="absolute left-0 top-full mt-1 z-30 bg-stone-950 border border-stone-700 shadow-xl py-1 min-w-[180px]" data-testid={`cut-cat-popover-${c.id}`}>
                      <div className="px-2 py-1 text-[9px] uppercase tracking-widest text-stone-500 border-b border-stone-800">Change category</div>
                      {CATEGORIES.map((opt) => (
                        <button
                          key={opt.id}
                          onClick={() => changeCategory(c, opt.id)}
                          className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-stone-900 transition-colors ${opt.id === c.category ? "text-orange-400" : "text-stone-300"}`}
                          data-testid={`cut-cat-opt-${c.id}-${opt.id}`}
                        >
                          {opt.id === c.category && <Check size={10} className="inline mr-1.5" />}
                          {opt.name}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              {c.event_name && <div className="text-[10px] uppercase tracking-widest text-orange-500/80 mb-1 font-mono-tc">{c.event_name}{c.performance ? ` · ${c.performance}` : ""}</div>}
              {!c.event_name && c.performance && <div className="text-[10px] uppercase tracking-widest text-orange-500/80 mb-1 font-mono-tc">{c.performance}</div>}
              <div className="flex items-center gap-1.5 mb-1 group/subedit">
                {c.sub_label ? (
                  <div className="text-sm text-stone-200 truncate flex-1">{c.sub_label}</div>
                ) : (
                  <div className="text-[11px] text-stone-600 italic flex-1">no title</div>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onRenameCut && onRenameCut(c); }}
                  className="text-stone-500 hover:text-orange-400 p-0.5 transition-colors opacity-0 group-hover/subedit:opacity-100 focus:opacity-100"
                  title="Rename this cut (updates the rendered clip too)"
                  data-testid={`cut-rename-btn-${c.id}`}
                >
                  <Pencil size={10} />
                </button>
              </div>
              {(c.rider_horse_name || c.heeler_name) && (
                <div className="text-xs text-stone-500 mb-1">
                  {c.rider_horse_name}{c.rider_horse_name && c.heeler_name ? " & " : ""}{c.heeler_name}
                </div>
              )}
              {c.horse_name && <div className="text-xs text-stone-500 mb-1">🐎 {c.horse_name}</div>}
              {c.owner_name && <div className="text-xs text-stone-500 mb-1">👤 Owner: {c.owner_name}</div>}
              {(c.run_time != null || c.score != null) && (
                <div className="flex items-center gap-2 mb-1" data-testid={`cut-run-stats-${c.id}`}>
                  {c.run_time != null && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono-tc bg-amber-950/50 border border-amber-700/50 text-amber-300">
                      <span className="text-amber-500/70 uppercase text-[8px] tracking-widest">Time</span> {Number(c.run_time).toFixed(2)}s
                    </span>
                  )}
                  {c.score != null && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono-tc bg-emerald-950/50 border border-emerald-700/50 text-emerald-300">
                      <span className="text-emerald-500/70 uppercase text-[8px] tracking-widest">Score</span> {Number(c.score)}
                    </span>
                  )}
                </div>
              )}
              {c.notes && <div className="text-xs text-stone-500 italic mb-2">{c.notes}</div>}
              <CutDetailsEditor
                cut={c}
                onRefresh={onRefresh}
                onScanScoreboard={onScanScoreboard}
                scoreboardBusy={!!(scoreboardBusy && scoreboardBusy[c.id])}
              />
              <button
                onClick={() => {
                  setEditingCutId(editingCutId === c.id ? null : c.id);
                  // Auto-preview the range so the user can see what they're editing
                  onSeek(c.start_time, true, c.end_time);
                }}
                className={`font-mono-tc text-xs flex justify-between w-full mb-2 px-1.5 py-1 border transition-colors ${
                  editingCutId === c.id
                    ? "border-orange-700 text-orange-300 bg-orange-900/10"
                    : "border-stone-800 text-stone-400 hover:border-stone-600 hover:text-stone-200"
                }`}
                title="Click to preview & drag-trim in/out"
                data-testid={`cut-time-row-${c.id}`}
              >
                <span>{fmtTime(c.start_time)} → {fmtTime(c.end_time)}</span>
                <span className="text-orange-500">{fmtDuration(c.end_time - c.start_time)}</span>
              </button>
              {editingCutId === c.id && (
                <CutTrimEditor
                  cut={c}
                  duration={duration}
                  isRendered={isGenerated}
                  current={current}
                  onSeek={onSeek}
                  onSave={(s, e) => saveCutTimes(c, s, e)}
                  onCancel={() => setEditingCutId(null)}
                />
              )}
              {!isGenerated && (
                <button
                  onClick={() => onGenerate([c.id])}
                  disabled={generating || isGenerating}
                  className="btn-secondary !py-1 !px-2 w-full text-[11px] inline-flex items-center justify-center gap-1.5 hover:!border-orange-700 hover:!text-orange-400"
                  data-testid={`generate-single-${c.id}`}
                >
                  {isGenerating ? <Loader2 size={11} className="animate-spin" /> : <Film size={11} />}
                  {isGenerating ? "Cutting…" : "Generate this clip"}
                </button>
              )}
              {isGenerated && clipsByCutId.get(c.id) && (
                <div className="mt-2 flex items-center gap-1.5 relative" data-testid={`cut-assign-row-${c.id}`}>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 flex-1">
                    Send this clip to…
                  </div>
                  <AssignGalleryButton
                    clipId={clipsByCutId.get(c.id).id}
                    clip={clipsByCutId.get(c.id)}
                  />
                </div>
              )}
            </div>
          );})
        )}
      </div>

      <div className="border-t border-stone-800 p-4">
        {(() => {
          // Dead-spot cuts never generate clips, so exclude them from the pending count
          const liveCuts = cuts.filter((c) => !c.is_dead_spot);
          const pending = liveCuts.filter((c) => !clipsByCutId.has(c.id)).length;
          const allDone = liveCuts.length > 0 && pending === 0;
          return (
            <button
              onClick={() => onGenerate()}
              disabled={generating || liveCuts.length === 0 || allDone}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="generate-clips-btn"
            >
              {generating ? <Loader2 size={14} className="animate-spin" /> : <Film size={14} />}
              {generating
                ? "Cutting clips…"
                : allDone
                  ? "All clips generated"
                  : `Generate ${pending} pending clip${pending !== 1 ? "s" : ""}`}
            </button>
          );
        })()}
        <div className="text-[10px] text-stone-500 uppercase tracking-wider mt-2 text-center">
          Re-encoded H.264 · MP4 · accurate to frame
        </div>
      </div>
    </div>
  );
}

function CutDetailsEditor({ cut, onRefresh, onScanScoreboard, scoreboardBusy }) {
  const [open, setOpen] = useState(false);
  const [eventName, setEventName] = useState(cut.event_name || "");
  const [performance, setPerformance] = useState(cut.performance || "");
  const [sub, setSub] = useState(cut.sub_label || "");
  const [rider, setRider] = useState(cut.rider_horse_name || "");
  const [heeler, setHeeler] = useState(cut.heeler_name || "");
  const [horse, setHorse] = useState(cut.horse_name || "");
  const [owner, setOwner] = useState(cut.owner_name || "");
  const [notes, setNotes] = useState(cut.notes || "");
  const [runTime, setRunTime] = useState(cut.run_time != null ? String(cut.run_time) : "");
  const [score, setScore] = useState(cut.score != null ? String(cut.score) : "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEventName(cut.event_name || "");
    setPerformance(cut.performance || "");
    setSub(cut.sub_label || "");
    setRider(cut.rider_horse_name || "");
    setHeeler(cut.heeler_name || "");
    setHorse(cut.horse_name || "");
    setOwner(cut.owner_name || "");
    setNotes(cut.notes || "");
    setRunTime(cut.run_time != null ? String(cut.run_time) : "");
    setScore(cut.score != null ? String(cut.score) : "");
  }, [cut.event_name, cut.performance, cut.sub_label, cut.rider_horse_name, cut.heeler_name, cut.horse_name, cut.owner_name, cut.notes, cut.run_time, cut.score]);

  const save = async () => {
    setSaving(true);
    try {
      const parseNum = (s) => {
        const t = (s || "").trim();
        if (t === "") return null;
        const n = parseFloat(t);
        return isNaN(n) ? null : n;
      };
      await api.put(`/cuts/${cut.id}`, {
        event_name: eventName,
        performance: performance,
        sub_label: sub,
        rider_horse_name: rider,
        heeler_name: heeler,
        horse_name: horse,
        owner_name: owner,
        notes,
        run_time: parseNum(runTime),
        score: parseNum(score),
      });
      toast.success("Details updated");
      setOpen(false);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error("Failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="text-[10px] uppercase tracking-wider text-stone-500 hover:text-orange-400 inline-flex items-center gap-1 mb-2"
        title="Edit sub-label, rider, horse, notes"
        data-testid={`cut-edit-details-${cut.id}`}
      >
        <Pencil size={9} /> Edit details
      </button>
    );
  }

  return (
    <div className="mb-2 p-2 bg-stone-950 border border-orange-700/60 space-y-2" data-testid={`cut-details-editor-${cut.id}`}>
      <div>
        <div className="text-[9px] uppercase tracking-widest text-stone-500 mb-1">Performance</div>
        <AutocompleteInput
          field="event_name"
          sourceEndpoint="/events"
          sourceMap={(d) => (d.events || []).map((e) => e.name)}
          value={eventName}
          onChange={setEventName}
          placeholder="Pick a performance"
          className="field-input !py-1 !text-xs"
          data-testid={`cut-edit-event-${cut.id}`}
        />
      </div>
      <div>
        <div className="text-[9px] uppercase tracking-widest text-stone-500 mb-1">Round / Session</div>
        <AutocompleteInput
          field="performance"
          value={performance}
          onChange={setPerformance}
          placeholder="Slack · Round 1 · Short Go"
          className="field-input !py-1 !text-xs"
          data-testid={`cut-edit-performance-${cut.id}`}
        />
      </div>
      <div>
        <div className="text-[9px] uppercase tracking-widest text-stone-500 mb-1">Sub-label</div>
        <AutocompleteInput
          field="sub_label"
          value={sub}
          onChange={setSub}
          placeholder="clean run, broke barrier…"
          className="field-input !py-1 !text-xs"
          data-testid={`cut-edit-sublabel-${cut.id}`}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-stone-500 mb-1">Rider / Header</div>
          <AutocompleteInput
            field="rider"
            value={rider}
            onChange={setRider}
            placeholder="Sarah Johnson"
            className="field-input !py-1 !text-xs"
            data-testid={`cut-edit-rider-${cut.id}`}
          />
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-stone-500 mb-1">Heeler</div>
          <AutocompleteInput
            field="heeler"
            value={heeler}
            onChange={setHeeler}
            placeholder="team roping only"
            className="field-input !py-1 !text-xs"
            data-testid={`cut-edit-heeler-${cut.id}`}
          />
        </div>
        <div className="col-span-2">
          <div className="text-[9px] uppercase tracking-widest text-stone-500 mb-1">Horse</div>
          <AutocompleteInput
            field="horse"
            value={horse}
            onChange={setHorse}
            placeholder="Dusty"
            className="field-input !py-1 !text-xs"
            data-testid={`cut-edit-horse-${cut.id}`}
          />
        </div>
        <div className="col-span-2">
          <div className="text-[9px] uppercase tracking-widest text-stone-500 mb-1">Owner</div>
          <AutocompleteInput
            field="owner"
            value={owner}
            onChange={setOwner}
            placeholder="horse owner / breeder"
            className="field-input !py-1 !text-xs"
            data-testid={`cut-edit-owner-${cut.id}`}
          />
        </div>
      </div>
      <div>
        <div className="text-[9px] uppercase tracking-widest text-stone-500 mb-1">Notes</div>
        <AutocompleteInput
          as="textarea"
          field="notes"
          value={notes}
          onChange={setNotes}
          rows={2}
          placeholder="optional"
          className="field-input !py-1 !text-xs"
          data-testid={`cut-edit-notes-${cut.id}`}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-stone-500 mb-1">Time <span className="normal-case text-stone-600">(sec)</span></div>
          <input
            type="number"
            step="0.01"
            min="0"
            inputMode="decimal"
            value={runTime}
            onChange={(e) => setRunTime(e.target.value)}
            placeholder="14.72"
            className="field-input !py-1 !text-xs"
            data-testid={`cut-edit-time-${cut.id}`}
          />
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-stone-500 mb-1">Score</div>
          <input
            type="number"
            step="0.5"
            inputMode="decimal"
            value={score}
            onChange={(e) => setScore(e.target.value)}
            placeholder="87.5"
            className="field-input !py-1 !text-xs"
            data-testid={`cut-edit-score-${cut.id}`}
          />
        </div>
      </div>
      <div className="flex gap-1.5">
        <button
          onClick={() => {
            setOpen(false);
            setEventName(cut.event_name || "");
            setPerformance(cut.performance || "");
            setSub(cut.sub_label || "");
            setRider(cut.rider_horse_name || "");
            setHeeler(cut.heeler_name || "");
            setHorse(cut.horse_name || "");
            setOwner(cut.owner_name || "");
            setNotes(cut.notes || "");
            setRunTime(cut.run_time != null ? String(cut.run_time) : "");
            setScore(cut.score != null ? String(cut.score) : "");
          }}
          className="flex-1 py-1 text-[10px] uppercase tracking-wider border border-stone-700 text-stone-400 hover:text-stone-200 hover:border-stone-500"
          data-testid={`cut-edit-cancel-${cut.id}`}
        >
          Cancel
        </button>
        {onScanScoreboard && (
          <button
            onClick={() => onScanScoreboard(cut.id, { force: true, showEmpty: true })}
            disabled={!!scoreboardBusy}
            title="Read the on-screen scoreboard graphic (rider · horse · time · score) with Gemini vision and fill any empty fields"
            className="py-1 px-2 text-[10px] uppercase tracking-wider border border-sky-800/60 bg-sky-900/25 hover:bg-sky-800/50 text-sky-300 inline-flex items-center gap-1 disabled:opacity-40"
            data-testid={`cut-edit-scoreboard-${cut.id}`}
          >
            {scoreboardBusy ? <Loader2 size={10} className="animate-spin" /> : <Wand2 size={10} />}
            {scoreboardBusy ? "Reading…" : "Scan scoreboard"}
          </button>
        )}
        <button
          onClick={save}
          disabled={saving}
          className="flex-1 py-1 text-[10px] uppercase tracking-wider border border-orange-700 bg-orange-700 text-white hover:bg-orange-600 disabled:opacity-40"
          data-testid={`cut-edit-save-${cut.id}`}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function CutTrimEditor({ cut, duration, isRendered, current, onSeek, onSave, onCancel }) {
  // Inline drag-handle trimmer. The rail is EXACTLY the clip's own start→end
  // window — handles can't move past either boundary.  You can only shorten
  // the clip here, never extend it.  To extend, edit the cut from the main
  // timeline (drag its edge handles) or delete & re-mark.
  const origStart = Number(cut.start_time);
  const origEnd = Number(cut.end_time);
  const railStart = origStart;
  const railEnd = origEnd;
  const railSpan = Math.max(0.1, railEnd - railStart);

  const [inT, setInT]   = useState(origStart);
  const [outT, setOutT] = useState(origEnd);
  const [dragging, setDragging] = useState(null); // 'in' | 'out' | null
  const [detecting, setDetecting] = useState(false);
  const railRef = useRef(null);

  const pctOf = (t) => Math.max(0, Math.min(100, ((t - railStart) / railSpan) * 100));

  const autoDetectBlack = async () => {
    setDetecting(true);
    try {
      const r = await api.post(`/cuts/${cut.id}/blackdetect`);
      const newIn = Math.max(origStart, Math.min(Number(r.data.suggested_start), origEnd - 0.3));
      const newOut = Math.min(origEnd, Math.max(Number(r.data.suggested_end), newIn + 0.3));
      setInT(Number(newIn.toFixed(2)));
      setOutT(Number(newOut.toFixed(2)));
      const head = Number(r.data.head_trim || 0);
      const tail = Number(r.data.tail_trim || 0);
      if (head < 0.1 && tail < 0.1) {
        toast.info("No leading/trailing black frames found");
      } else {
        toast.success(`Deadspace found · head ${head.toFixed(1)}s · tail ${tail.toFixed(1)}s`);
      }
    } catch (err) {
      toast.error("Auto-detect failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setDetecting(false);
    }
  };

  const onPointerMove = useCallback((evt) => {
    if (!dragging || !railRef.current) return;
    const rect = railRef.current.getBoundingClientRect();
    const x = (evt.clientX ?? evt.touches?.[0]?.clientX) - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    const t = railStart + ratio * railSpan;
    if (dragging === "in") {
      // Clamp strictly to [origStart, outT - 0.3]
      const clamped = Math.max(origStart, Math.min(t, outT - 0.3));
      setInT(Number(clamped.toFixed(2)));
    } else if (dragging === "out") {
      // Clamp strictly to [inT + 0.3, origEnd]
      const clamped = Math.min(origEnd, Math.max(t, inT + 0.3));
      setOutT(Number(clamped.toFixed(2)));
    }
  }, [dragging, inT, outT, origStart, origEnd, railStart, railSpan]);

  const stopDrag = useCallback(() => setDragging(null), []);

  useEffect(() => {
    if (!dragging) return;
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", stopDrag);
    window.addEventListener("pointercancel", stopDrag);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", stopDrag);
      window.removeEventListener("pointercancel", stopDrag);
    };
  }, [dragging, onPointerMove, stopDrag]);

  const newDur = Math.max(0, outT - inT);
  const inDelta = inT - origStart;
  const outDelta = outT - origEnd;
  const dirty = Math.abs(inDelta) > 0.05 || Math.abs(outDelta) > 0.05;

  const nudge = (which, sec) => {
    if (which === "in") {
      setInT((v) => {
        const next = Number(Math.max(railStart, Math.min(v + sec, outT - 0.3)).toFixed(2));
        // Scrub the source video to the new IN point so the user can SEE
        // the frame they're trimming to. Same idea when nudging OUT — jump
        // 0.2s before the new OUT so the last frame is visible.
        onSeek(next, false);
        return next;
      });
    } else {
      setOutT((v) => {
        const next = Number(Math.min(railEnd, Math.max(v + sec, inT + 0.3)).toFixed(2));
        onSeek(Math.max(0, next - 0.2), false);
        return next;
      });
    }
  };

  return (
    <div className="mb-2 p-2.5 bg-stone-950 border border-orange-700/60" data-testid={`trim-editor-${cut.id}`}>
      {isRendered && (
        <div className="mb-2 text-[10px] text-amber-400 leading-snug border border-amber-700/40 bg-amber-900/10 px-2 py-1.5">
          ⚠ Clip already rendered. Saving will <span className="font-bold">delete the old .mp4</span> and re-render at the new bounds.
        </div>
      )}
      {/* Rail */}
      <div
        ref={railRef}
        className="relative h-8 bg-stone-800 select-none touch-none cursor-pointer"
        onPointerDown={(e) => {
          // If the pointerdown originated on the IN or OUT handle we let the
          // handle's own onPointerDown take over (drag-trim).
          const tag = e.target?.getAttribute?.("data-testid") || "";
          if (tag.startsWith("trim-handle-in-") || tag.startsWith("trim-handle-out-")) return;
          // Any other pointerdown on the rail — including on the shaded region,
          // the selected region, or the playhead itself — should seek the
          // playhead to the clicked position. Prevent bubbling so the outer
          // cut-item onClick doesn't jump us back to the cut's start_time.
          e.stopPropagation();
          const rect = railRef.current.getBoundingClientRect();
          const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
          const t = railStart + ratio * railSpan;
          onSeek(t, false);
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Original cut shaded region (reference) */}
        <div
          className="absolute top-0 bottom-0 bg-stone-700/40 border-x border-stone-600/40"
          style={{ left: `${pctOf(origStart)}%`, right: `${100 - pctOf(origEnd)}%` }}
          title="Original cut range"
        />
        {/* Selected region */}
        <div
          className="absolute top-0 bottom-0 bg-orange-600/40 border-x-2 border-orange-500"
          style={{ left: `${pctOf(inT)}%`, right: `${100 - pctOf(outT)}%` }}
        />
        {/* IN handle */}
        <div
          onPointerDown={(e) => { e.preventDefault(); setDragging("in"); }}
          className={`absolute top-0 -ml-2 w-4 h-full cursor-ew-resize flex items-center justify-center bg-orange-500 hover:bg-orange-400 ${dragging === "in" ? "bg-orange-300" : ""}`}
          style={{ left: `${pctOf(inT)}%` }}
          title="Drag to set IN point"
          data-testid={`trim-handle-in-${cut.id}`}
        >
          <div className="w-0.5 h-3 bg-stone-950" />
        </div>
        {/* OUT handle */}
        <div
          onPointerDown={(e) => { e.preventDefault(); setDragging("out"); }}
          className={`absolute top-0 -ml-2 w-4 h-full cursor-ew-resize flex items-center justify-center bg-orange-500 hover:bg-orange-400 ${dragging === "out" ? "bg-orange-300" : ""}`}
          style={{ left: `${pctOf(outT)}%` }}
          title="Drag to set OUT point"
          data-testid={`trim-handle-out-${cut.id}`}
        >
          <div className="w-0.5 h-3 bg-stone-950" />
        </div>
        {/* Playhead indicator (ghost line — shows current video time so user
            can position playback then snap IN/OUT to it with one click) */}
        {current != null && current >= railStart && current <= railEnd && (
          <div
            className="absolute top-0 bottom-0 w-px bg-sky-400 pointer-events-none z-20"
            style={{ left: `${pctOf(current)}%` }}
            title={`Playhead @ ${fmtTime(current)}`}
            data-testid={`trim-playhead-${cut.id}`}
          >
            <div className="absolute -top-1 -left-1 w-2 h-2 bg-sky-400 rotate-45" />
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 mt-2 text-[10px] font-mono-tc">
        <div>
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-stone-500">IN</span>
            <span className={inDelta === 0 ? "text-stone-400" : inDelta > 0 ? "text-rose-400" : "text-emerald-400"}>
              {inDelta >= 0 ? "+" : ""}{inDelta.toFixed(1)}s
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => nudge("in", -0.5)} className="px-1.5 py-0.5 bg-stone-800 hover:bg-orange-700 text-stone-300 text-[10px]" data-testid={`trim-in-nudge-back-${cut.id}`}>−0.5</button>
            <button onClick={() => nudge("in", 0.5)} className="px-1.5 py-0.5 bg-stone-800 hover:bg-orange-700 text-stone-300 text-[10px]" data-testid={`trim-in-nudge-fwd-${cut.id}`}>+0.5</button>
            <button
              onClick={() => {
                if (current == null) return;
                const t = Math.max(railStart, Math.min(current, outT - 0.3));
                setInT(Number(t.toFixed(2)));
              }}
              disabled={current == null}
              className="px-1.5 py-0.5 bg-sky-900/40 border border-sky-700/60 hover:bg-sky-700 text-sky-300 text-[10px] disabled:opacity-30"
              title="Snap IN to current playhead"
              data-testid={`trim-in-snap-${cut.id}`}
            >
              ⤓ playhead
            </button>
            <button onClick={() => onSeek(inT, false)} className="ml-auto px-1.5 py-0.5 text-stone-500 hover:text-orange-400" title="Preview at IN">▶</button>
          </div>
          <div className="text-stone-500 text-[10px] mt-1">{fmtTime(inT)}</div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-stone-500">OUT</span>
            <span className={outDelta === 0 ? "text-stone-400" : outDelta < 0 ? "text-rose-400" : "text-emerald-400"}>
              {outDelta >= 0 ? "+" : ""}{outDelta.toFixed(1)}s
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => nudge("out", -0.5)} className="px-1.5 py-0.5 bg-stone-800 hover:bg-orange-700 text-stone-300 text-[10px]" data-testid={`trim-out-nudge-back-${cut.id}`}>−0.5</button>
            <button onClick={() => nudge("out", 0.5)} className="px-1.5 py-0.5 bg-stone-800 hover:bg-orange-700 text-stone-300 text-[10px]" data-testid={`trim-out-nudge-fwd-${cut.id}`}>+0.5</button>
            <button
              onClick={() => {
                if (current == null) return;
                const t = Math.min(railEnd, Math.max(current, inT + 0.3));
                setOutT(Number(t.toFixed(2)));
              }}
              disabled={current == null}
              className="px-1.5 py-0.5 bg-sky-900/40 border border-sky-700/60 hover:bg-sky-700 text-sky-300 text-[10px] disabled:opacity-30"
              title="Snap OUT to current playhead"
              data-testid={`trim-out-snap-${cut.id}`}
            >
              ⤓ playhead
            </button>
            <button onClick={() => onSeek(Math.max(railStart, outT - 1.5), false)} className="ml-auto px-1.5 py-0.5 text-stone-500 hover:text-orange-400" title="Preview just before OUT">▶</button>
          </div>
          <div className="text-stone-500 text-[10px] mt-1">{fmtTime(outT)}</div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-2 text-[10px] font-mono-tc">
        <span className="text-stone-500">New duration</span>
        <span className="text-orange-400">{fmtDuration(newDur)}</span>
      </div>

      <button
        onClick={autoDetectBlack}
        disabled={detecting || dragging}
        className="mt-2 w-full py-1.5 text-[10px] uppercase tracking-widest border border-sky-800/60 bg-sky-900/20 hover:bg-sky-800/40 text-sky-300 disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
        title="Scan this cut for leading/trailing black frames and nudge IN/OUT past them"
        data-testid={`trim-blackdetect-${cut.id}`}
      >
        {detecting ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
        {detecting ? "Scanning for black frames…" : "Auto-detect deadspace (black frames)"}
      </button>

      <div className="flex gap-1.5 mt-2">
        <button
          onClick={onCancel}
          className="flex-1 py-1.5 text-[11px] uppercase tracking-wider border border-stone-700 text-stone-400 hover:text-stone-200 hover:border-stone-500"
          data-testid={`trim-cancel-${cut.id}`}
        >
          Cancel
        </button>
        <button
          onClick={() => onSave(inT, outT)}
          disabled={!dirty || newDur < 0.5}
          className="flex-1 py-1.5 text-[11px] uppercase tracking-wider border border-orange-700 bg-orange-700 text-white hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`trim-save-${cut.id}`}
        >
          {isRendered ? "Save & re-render" : "Save trim"}
        </button>
      </div>
    </div>
  );
}


function HelpPanel({ onClose }) {
  return (
    <div className="bg-amber-950/30 border-y border-amber-800/40 px-4 py-3" data-testid="help-panel">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="font-display uppercase font-bold text-amber-400 text-sm tracking-wider flex items-center gap-2">
          <HelpCircle size={14} /> How to make good cuts (and train the AI)
        </div>
        <button onClick={onClose} className="text-amber-500/60 hover:text-amber-400" data-testid="dismiss-help">
          <X size={14} />
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-xs text-stone-300">
        <div>
          <div className="font-bold text-amber-300 uppercase tracking-wider text-[10px] mb-1.5">Marking a cut</div>
          <ol className="space-y-1 list-decimal list-inside text-stone-300/90">
            <li>Play / scrub to <span className="text-emerald-400">~1s before action starts</span></li>
            <li>Click <code className="bg-stone-900 px-1 text-emerald-400">Set IN</code></li>
            <li>Play to <span className="text-rose-400">~1s after run completes</span></li>
            <li>Click <code className="bg-stone-900 px-1 text-rose-400">Set OUT</code></li>
            <li>Fill category, sub-label, rider — then <code className="bg-stone-900 px-1 text-orange-400">+ Add Cut</code></li>
          </ol>
        </div>
        <div>
          <div className="font-bold text-amber-300 uppercase tracking-wider text-[10px] mb-1.5">What makes a "good" cut</div>
          <ul className="space-y-1 list-disc list-inside text-stone-300/90">
            <li>Clip length <span className="text-orange-400">5–60 seconds</span></li>
            <li>Lead-in: <span className="text-emerald-400">~1s before</span> action</li>
            <li>Trail-out: <span className="text-rose-400">~1s after</span> run completes</li>
            <li><b>Consistent sub-labels</b> ("clean run", "no-time", "fall")</li>
            <li>Fill rider/horse name when on-screen graphics show it</li>
            <li>Use Notes to explain "why" — feeds AI's prompt directly</li>
          </ul>
        </div>
        <div>
          <div className="font-bold text-amber-300 uppercase tracking-wider text-[10px] mb-1.5">How AI learns</div>
          <ul className="space-y-1 list-disc list-inside text-stone-300/90">
            <li>Every <b>manual cut</b> = auto-flagged "good example" for the AI</li>
            <li>Click <ThumbsUp size={10} className="inline text-emerald-400" /> on AI cuts you like — stronger signal</li>
            <li>Click <ThumbsDown size={10} className="inline text-rose-400" /> + delete on bad ones — "don't make these"</li>
            <li>Next AI Suggest gets your last 10 good + 5 bad as in-prompt examples</li>
            <li>~30 examples per category = noticeably "trained" feel</li>
          </ul>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-amber-800/40 text-[11px] text-stone-400 italic">
        Tip: For your first 3-5 source videos, lean on <b>manual cuts</b>. After ~30/category, AI Suggest starts feeling smart. Click the <HelpCircle size={11} className="inline" /> in the top bar anytime to bring this back.
      </div>
    </div>
  );
}

function EventEditModal({ video, onClose, onSaved }) {
  const [name, setName] = useState(video.event_name || "");
  const [date, setDate] = useState(video.event_date || "");
  const [association, setAssociation] = useState(video.association || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/videos/${video.id}`, { event_name: name, event_date: date, association });
      toast.success("Performance updated — title cards on future reels will use this");
      onSaved();
    } catch (err) {
      toast.error("Failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="event-edit-modal">
      <div className="bg-stone-950 border border-stone-700 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-800">
          <div className="font-display uppercase text-lg">Performance Details</div>
          <button onClick={onClose} className="btn-secondary" data-testid="event-edit-close"><X size={14} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="label-tiny block mb-1">Association</label>
            <AutocompleteInput field="association" value={association} onChange={setAssociation} className="field-input" placeholder="Canadian Cowboys Association" data-testid="event-edit-association" />
            <div className="text-[10px] text-stone-500 mt-1">Umbrella (e.g. CCA, WPRA) — many events can share one association.</div>
          </div>
          <div>
            <label className="label-tiny block mb-1">Performance</label>
            <AutocompleteInput field="event_name" value={name} onChange={setName} className="field-input" placeholder="Olds Open Jackpot · Round 1" data-testid="event-edit-name" />
          </div>
          <div>
            <label className="label-tiny block mb-1">Performance Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="field-input" data-testid="event-edit-date" />
          </div>
          <div className="text-[11px] text-stone-500">
            Shown on title cards in highlight reels and used to filter the Library by performance.
          </div>
        </div>
        <div className="px-5 pb-5 flex gap-2">
          <button onClick={onClose} className="btn-secondary flex-1" data-testid="event-edit-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary flex-1" data-testid="event-edit-save">
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
function VideoTile({ video, onOpen, onChanged }) {
  const [v, setV] = useState(video);
  useEffect(() => { setV(video); }, [video]);

  // Poll while compressing
  useEffect(() => {
    const inProgress = ["queued", "downloading", "encoding", "uploading"].includes(v.compress_status);
    if (!inProgress) return;
    const id = setInterval(async () => {
      try {
        const r = await api.get(`/videos/${v.id}`);
        setV(r.data);
        if (r.data.compress_status === "done" || r.data.compress_status === "failed") {
          clearInterval(id);
          onChanged && onChanged();
        }
      } catch {}
    }, 2500);
    return () => clearInterval(id);
  }, [v.compress_status, v.id, onChanged]);

  const inProgress = ["queued", "downloading", "encoding", "uploading"].includes(v.compress_status);
  const compressed = v.compress_status === "done";

  const compress = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Compress "${v.original_name}"?\n\nThis re-encodes to 720p and replaces the original on S3. Typically reduces size 5-7x. Takes ~1 minute per minute of footage.`)) return;
    try {
      await api.post(`/videos/${v.id}/compress`);
      const r = await api.get(`/videos/${v.id}`);
      setV(r.data);
      toast.info("Compression started");
    } catch (err) {
      toast.error("Failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  const remove = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${v.original_name}" and ALL its clips? This cannot be undone.`)) return;
    try {
      const r = await api.delete(`/videos/${v.id}`);
      toast.success(`Deleted · ${r.data.clips_deleted || 0} clip${r.data.clips_deleted === 1 ? "" : "s"} removed`);
      onChanged && onChanged();
    } catch (err) {
      toast.error("Delete failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  return (
    <div
      className="text-left border border-stone-800 hover:border-orange-700 p-3 transition-colors cursor-pointer relative group"
      onClick={onOpen}
      data-testid={`existing-video-${v.id}`}
    >
      <button
        onClick={remove}
        className="absolute top-2 right-2 p-1.5 text-stone-600 hover:text-rose-400 hover:bg-rose-500/10 transition-colors opacity-0 group-hover:opacity-100"
        title="Delete video and all its clips"
        data-testid={`delete-video-${v.id}`}
      >
        <Trash2 size={13} />
      </button>
      <div className="font-display uppercase font-semibold truncate pr-7">{v.original_name}</div>
      <div className="text-xs text-stone-500 font-mono-tc mt-1 flex items-center gap-2">
        <span>{fmtDuration(v.duration)}</span>
        <span>·</span>
        <span>{v.category?.replace(/_/g, " ") || "—"}</span>
        {v.size_bytes > 0 && (<><span>·</span><span>{fmtBytes(v.size_bytes)}</span></>)}
        {compressed && (
          <span className="ml-1 text-[9px] text-emerald-400 border border-emerald-500/40 px-1.5 py-0.5 uppercase tracking-wider">Compressed</span>
        )}
      </div>
      {(v.event_name || v.event_date) && (
        <div className="text-xs text-orange-400 font-mono-tc mt-1 truncate" title={`${v.event_name || ""} ${v.event_date || ""}`.trim()}>
          {v.event_name || ""}{v.event_name && v.event_date ? " · " : ""}{v.event_date || ""}
        </div>
      )}

      {inProgress && (
        <div className="mt-2 pt-2 border-t border-stone-800" data-testid={`compress-progress-${v.id}`}>
          <div className="flex items-center justify-between text-[10px] mb-1">
            <span className="text-orange-400 uppercase tracking-widest font-bold">
              {v.compress_status === "queued" && "Queued"}
              {v.compress_status === "downloading" && "Downloading"}
              {v.compress_status === "encoding" && "Encoding 720p"}
              {v.compress_status === "uploading" && "Stitching final"}
            </span>
            <span className="font-mono-tc text-stone-300">{v.compress_progress || 0}%</span>
          </div>
          <div className="h-1 bg-stone-900 overflow-hidden">
            <div className="h-full bg-orange-500 transition-all" style={{ width: `${v.compress_progress || 0}%` }} />
          </div>
          {v.compress_manifest && v.compress_manifest.total_chunks > 0 && (
            <div className="text-[9px] font-mono-tc text-stone-500 mt-1">
              Chunk {(v.compress_manifest.chunks || []).filter(c => c.status === "done").length}/{v.compress_manifest.total_chunks}
              · resumable
            </div>
          )}
        </div>
      )}

      {!inProgress && !compressed && v.size_bytes > 100 * 1024 * 1024 && (
        <div className="mt-2 pt-2 border-t border-stone-800 text-[10px] text-stone-500 uppercase tracking-widest" data-testid={`compress-disabled-${v.id}`}>
          Large file · compress externally before upload
        </div>
      )}

      {!inProgress && v.needs_transcode && (
        <div className="mt-2 pt-2 border-t border-amber-800/50" data-testid={`needs-transcode-${v.id}`}>
          <div className="text-[10px] text-amber-400 uppercase tracking-widest mb-1.5">
            ⚠ Not browser-playable ({v.needs_transcode_reason || "unknown format"})
          </div>
          <button
            onClick={compress}
            className="w-full py-1.5 text-[10px] uppercase tracking-widest border border-amber-700 bg-amber-900/20 hover:bg-amber-800/30 text-amber-200"
            data-testid={`transcode-btn-${v.id}`}
          >
            Convert to MP4 / H.264
          </button>
        </div>
      )}
      {compressed && v.compress_size_before > 0 && v.compress_size_after > 0 && (
        <div className="text-[10px] font-mono-tc text-emerald-400 mt-1.5">
          {fmtBytes(v.compress_size_before)} → {fmtBytes(v.compress_size_after)} ({Math.round((1 - v.compress_size_after/v.compress_size_before) * 100)}% smaller)
        </div>
      )}

      {v.compress_status === "failed" && (
        <div className="text-[10px] text-rose-400 mt-1.5" title={v.compress_error}>Compress failed</div>
      )}
    </div>
  );
}

function TrackRiderModal({ video, currentTime, videoSrc, onClose, onDone }) {
  const navigate = useNavigate();
  const previewRef = useRef(null);
  const [tFrame, setTFrame] = useState(currentTime || 0);
  const [bbox, setBbox] = useState(null); // current draft box: {x,y,w,h} normalized
  const [drawStart, setDrawStart] = useState(null);
  const [drawMode, setDrawMode] = useState(false); // false = scrub the video, true = drag a box
  const [refs, setRefs] = useState([]); // confirmed references: [{frame_time, bbox}]
  const [riderName, setRiderName] = useState("");
  const [category, setCategory] = useState(video.category || "barrel_racing");
  const [job, setJob] = useState(null);
  const [phase, setPhase] = useState("draw"); // draw | running | done | failed
  // Identity Library state
  const [libraryCount, setLibraryCount] = useState(0);
  const [useLibrary, setUseLibrary] = useState(false);

  // Lookup saved frames every time the rider name settles
  useEffect(() => {
    const t = setTimeout(async () => {
      const n = riderName.trim();
      if (!n) { setLibraryCount(0); setUseLibrary(false); return; }
      try {
        const r = await api.get(`/rider/${encodeURIComponent(n)}/training-frames`);
        const cnt = (r.data.frames || []).length;
        setLibraryCount(cnt);
        if (cnt > 0) setUseLibrary(true);
      } catch {
        setLibraryCount(0);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [riderName]);

  useEffect(() => {
    if (previewRef.current) {
      const v = previewRef.current;
      const onLoaded = () => { v.currentTime = currentTime || 0; };
      v.addEventListener("loadedmetadata", onLoaded);
      return () => v.removeEventListener("loadedmetadata", onLoaded);
    }
  }, [currentTime]);

  const onTimeChanged = () => {
    if (previewRef.current) setTFrame(previewRef.current.currentTime);
  };

  const onMouseDown = (e) => {
    if (phase !== "draw" || !drawMode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setDrawStart({ x, y });
    setBbox({ x, y, w: 0, h: 0 });
  };
  const onMouseMove = (e) => {
    if (!drawStart) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = (e.clientX - rect.left) / rect.width;
    const cy = (e.clientY - rect.top) / rect.height;
    const x = Math.min(drawStart.x, cx);
    const y = Math.min(drawStart.y, cy);
    const w = Math.abs(cx - drawStart.x);
    const h = Math.abs(cy - drawStart.y);
    setBbox({ x, y, w, h });
  };
  const onMouseUp = () => setDrawStart(null);

  const captureReference = () => {
    if (!bbox || bbox.w < 0.02 || bbox.h < 0.02) {
      toast.error("Draw a box around the rider first");
      return;
    }
    if (refs.length >= 3) {
      toast.error("3 reference frames is the maximum");
      return;
    }
    setRefs([...refs, { frame_time: tFrame, bbox: { ...bbox } }]);
    setBbox(null);
    setDrawMode(false); // auto-snap back to scrub so the user can seek forward
    toast.success(`Reference ${refs.length + 1} captured · scrub to a new frame, then "Draw Box" again`);
  };

  const removeRef = (i) => setRefs(refs.filter((_, idx) => idx !== i));

  const startTrack = async () => {
    // Auto-capture the current draft box if user hasn't pressed "Add Reference"
    let allRefs = refs;
    const wantLibrary = useLibrary && libraryCount > 0;
    if (allRefs.length === 0 && !wantLibrary) {
      if (!bbox || bbox.w < 0.02 || bbox.h < 0.02) {
        toast.error("Draw a box around the rider on the video — or enable Use Saved Library");
        return;
      }
      allRefs = [{ frame_time: tFrame, bbox: { ...bbox } }];
    }
    if (!riderName.trim()) {
      toast.error("Enter the rider/horse name");
      return;
    }
    setPhase("running");
    try {
      const r = await api.post(`/videos/${video.id}/track-rider`, {
        rider_name: riderName.trim(),
        category,
        reference_frames: allRefs.map((rr) => ({
          frame_time: rr.frame_time,
          bbox: [rr.bbox.x, rr.bbox.y, rr.bbox.w, rr.bbox.h],
        })),
        use_library: wantLibrary,
      });
      const jobId = r.data.job_id;
      const deadline = Date.now() + 20 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 3000));
        const j = await api.get(`/rider-track-jobs/${jobId}`);
        setJob(j.data);
        if (j.data.status === "done") { setPhase("done"); onDone && onDone(jobId); return; }
        if (j.data.status === "failed") { setPhase("failed"); return; }
      }
      setPhase("failed");
      toast.error("Tracking timed out");
    } catch (err) {
      setPhase("failed");
      toast.error("Failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="track-rider-modal">
      <div className="bg-stone-950 border border-stone-700 max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-800">
          <div className="font-display uppercase text-xl flex items-center gap-2">
            <Crosshair size={16} className="text-orange-500" /> Track Rider
            <span className="ml-2 text-[10px] uppercase tracking-widest text-emerald-400 border border-emerald-700/40 px-1.5 py-0.5">v2 · dual-pass</span>
          </div>
          <button onClick={onClose} className="btn-secondary" data-testid="track-close"><X size={14} /></button>
        </div>

        {phase === "draw" && (
          <>
            <div className="px-4 py-3 text-xs text-stone-400 border-b border-stone-800 flex items-center gap-3">
              <span className="text-orange-400 font-bold uppercase tracking-widest">Tip</span>
              <span>Scrub to a clear frame · click <strong className="text-stone-200">Draw Box</strong> · drag a rectangle around the rider · click <strong className="text-stone-200">+ Add Reference</strong>. Capture 2–3 angles for best accuracy.</span>
            </div>
            <div className="bg-black relative" data-testid="track-canvas-wrap">
              <video
                ref={previewRef}
                src={videoSrc}
                className="w-full max-h-[55vh] object-contain"
                controls
                onTimeUpdate={onTimeChanged}
                onPause={onTimeChanged}
                onSeeked={onTimeChanged}
                playsInline
              />
              {/* Overlay is pointer-events-none unless drawMode is on, so the
                  native video controls (play/seek) keep working. */}
              <div
                className={`absolute inset-0 ${drawMode ? "cursor-crosshair" : "pointer-events-none"}`}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
                data-testid="track-overlay"
              >
                {bbox && (
                  <div
                    className="absolute border-2 border-orange-500 bg-orange-500/15"
                    style={{
                      left: `${bbox.x * 100}%`,
                      top: `${bbox.y * 100}%`,
                      width: `${bbox.w * 100}%`,
                      height: `${bbox.h * 100}%`,
                    }}
                  />
                )}
              </div>
              {/* Mode chip floats over the video, top-right */}
              <div className="absolute top-2 right-2 pointer-events-none">
                <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2 py-1 ${drawMode ? "bg-orange-600/95 text-white" : "bg-stone-900/85 text-stone-300 border border-stone-700"}`}>
                  {drawMode ? <><Crosshair size={11} /> Drawing</> : <><Play size={11} /> Scrub Mode</>}
                </span>
              </div>
            </div>
            <div className="p-4 space-y-3">
              {/* Mode toggle + captured references */}
              <div className="flex items-center gap-2 flex-wrap" data-testid="track-refs-row">
                <button
                  onClick={() => setDrawMode(!drawMode)}
                  className={`btn-secondary text-xs inline-flex items-center gap-1.5 ${drawMode ? "!border-orange-700 !text-orange-400" : "hover:!border-orange-700 hover:!text-orange-400"}`}
                  data-testid="track-toggle-draw"
                >
                  {drawMode ? <><Play size={12} /> Switch to Scrub</> : <><Crosshair size={12} /> Draw Box</>}
                </button>
                <div className="h-5 w-px bg-stone-700" />
                {refs.map((r, i) => (
                  <div key={i} className="inline-flex items-center gap-1.5 bg-orange-700/15 border border-orange-700/50 px-2 py-1 text-xs font-mono-tc text-orange-300">
                    <Crosshair size={11} /> Ref {i + 1} @ {fmtTime(r.frame_time)}
                    <button onClick={() => removeRef(i)} className="ml-1 text-orange-500/70 hover:text-rose-400" data-testid={`track-ref-remove-${i}`}><X size={11} /></button>
                  </div>
                ))}
                {refs.length < 3 && (
                  <button
                    onClick={captureReference}
                    disabled={!bbox || bbox.w < 0.02 || bbox.h < 0.02}
                    className="btn-secondary text-xs inline-flex items-center gap-1.5 hover:!border-orange-700 hover:!text-orange-400 disabled:opacity-40"
                    data-testid="track-add-ref"
                  >
                    <Plus size={12} /> Add Reference {refs.length + 1}/3
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-tiny block mb-1">Rider / Horse name</label>
                  <AutocompleteInput field="rider" value={riderName} onChange={setRiderName} className="field-input" placeholder="Sarah Johnson · Dusty" data-testid="track-rider-name" />
                </div>
                <div>
                  <label className="label-tiny block mb-1">Discipline</label>
                  <select value={category} onChange={(e) => setCategory(e.target.value)} className="field-input" data-testid="track-category">
                    {CATEGORIES.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                  </select>
                </div>
              </div>

              {libraryCount > 0 && (
                <label className="flex items-start gap-2 text-xs text-stone-300 border border-emerald-700/40 bg-emerald-900/15 p-2.5" data-testid="track-use-library-row">
                  <input
                    type="checkbox"
                    checked={useLibrary}
                    onChange={(e) => setUseLibrary(e.target.checked)}
                    className="mt-0.5"
                    data-testid="track-use-library"
                  />
                  <span>
                    <span className="text-emerald-400 font-bold uppercase tracking-widest">✓ Saved Identity Library found</span> — {libraryCount} reference frame{libraryCount === 1 ? "" : "s"} on file for "{riderName.trim()}". Include them for the strongest fingerprint.
                  </span>
                </label>
              )}
              <div className="font-mono-tc text-[11px] text-stone-500">
                Current frame {fmtTime(tFrame)} · Draft box: {bbox ? `${(bbox.x*100).toFixed(0)}/${(bbox.y*100).toFixed(0)} · ${(bbox.w*100).toFixed(0)}×${(bbox.h*100).toFixed(0)}` : "(none — drag to draw)"}
              </div>
            </div>
            <div className="px-4 pb-4 flex gap-2 border-t border-stone-800 pt-3">
              <button onClick={() => setBbox(null)} className="btn-secondary" data-testid="track-clear-box">Clear Box</button>
              <div className="flex-1" />
              <button onClick={onClose} className="btn-secondary" data-testid="track-cancel">Cancel</button>
              <button onClick={startTrack} className="btn-primary inline-flex items-center gap-2" data-testid="track-start">
                <Sparkles size={14} /> Find Every Appearance
              </button>
            </div>
          </>
        )}

        {phase === "running" && (
          <div className="p-8 text-center" data-testid="track-running">
            <Loader2 size={28} className="animate-spin mx-auto text-orange-500 mb-4" />
            <div className="font-display uppercase text-2xl font-bold mb-2">Tracking {riderName}</div>
            <div className="text-stone-400 text-sm mb-5 min-h-[1.25rem]">
              {job?.stage === "downloading" && "Downloading source video…"}
              {job?.stage === "preparing" && "Cropping reference frame(s) & building summary…"}
              {job?.stage === "fingerprinting" && "Building structured rider fingerprint…"}
              {job?.stage === "scanning_pass_1" && "Pass 1/2 · Gemini scanning the reel…"}
              {job?.stage === "scanning_pass_2" && `Pass 2/2 · Cross-checking (Pass 1 found ${job?.pass_a_count ?? "?"} candidates)`}
              {job?.stage === "merging" && `Merging — keeping only matches confirmed by both passes…`}
              {job?.stage === "parsing" && "Creating cuts…"}
              {(!job || !job.stage) && "Starting…"}
            </div>
            <div className="h-1.5 bg-stone-900 max-w-sm mx-auto">
              <div className="h-full bg-orange-500 transition-all" style={{ width: `${job?.progress || 5}%` }} />
            </div>
            <div className="font-mono-tc text-xs text-stone-500 mt-3">{job?.progress || 5}%</div>
          </div>
        )}

        {phase === "done" && (
          <div className="p-8 text-center" data-testid="track-done">
            <Check size={36} className="text-emerald-400 mx-auto mb-3" />
            <div className="font-display uppercase text-2xl font-bold mb-2">{job?.cuts_created || 0} cut{job?.cuts_created !== 1 ? "s" : ""} added</div>

            {(job?.cuts_created || 0) > 0 ? (
              <>
                <div className="text-stone-400 text-sm mb-3">
                  Every appearance of <span className="text-orange-400">{riderName}</span> has been added to this video's <span className="text-stone-200">cut list</span>.
                </div>
                <div className="text-amber-400/90 text-xs mb-4 max-w-md mx-auto leading-relaxed border border-amber-700/40 bg-amber-900/10 p-3">
                  Next step: open the Studio editor, review the new cuts, then click <span className="font-bold">Generate Clips</span>. Your rider only appears on <span className="font-mono-tc">/riders</span> and in the Library <span className="underline">after</span> the clips have been rendered.
                </div>
              </>
            ) : (
              <div className="text-stone-400 text-sm mb-4 max-w-md mx-auto">
                The AI did not confidently identify <span className="text-orange-400">{riderName}</span> in this video. Try adding more reference frames (different angles) or saving frames to the Identity Library and re-running.
              </div>
            )}

            {job?.single_pass_fallback && (
              <div className="text-amber-400 text-xs mb-3 max-w-md mx-auto">
                ⚠ Only one of the two AI passes returned matches — these are likely candidates but each cut is flagged <span className="font-mono-tc">review</span> so you can verify before rendering.
              </div>
            )}
            {job?.review_count > 0 && !job?.single_pass_fallback && (
              <div className="text-amber-400 text-xs mb-3 max-w-md mx-auto">
                ⚠ {job.review_count} cut{job.review_count !== 1 ? "s" : ""} flagged "review" (low confidence) — please double-check before generating clips.
              </div>
            )}

            <div className="text-[10px] font-mono-tc text-stone-600 mb-5">
              Pass A: {job?.pass_a_count ?? 0} · Pass B: {job?.pass_b_count ?? 0} · Cuts added: {job?.cuts_created || 0}
            </div>

            <div className="flex items-center justify-center gap-3 flex-wrap">
              {(job?.cuts_created || 0) > 0 && (
                <button
                  onClick={() => {
                    onClose();
                    navigate(`/studio/${video.id}`);
                  }}
                  className="btn-primary"
                  data-testid="track-open-studio"
                >
                  Open in Studio →
                </button>
              )}
              <button onClick={onClose} className="btn-secondary" data-testid="track-done-close">Close</button>
            </div>
          </div>
        )}

        {phase === "failed" && (
          <div className="p-8 text-center" data-testid="track-failed">
            <X size={36} className="text-rose-400 mx-auto mb-3" />
            <div className="font-display uppercase text-2xl font-bold mb-2">Tracking Failed</div>
            <div className="text-stone-400 text-sm mb-5">{job?.error || "Unknown error"}</div>
            <button onClick={onClose} className="btn-secondary">Close</button>
          </div>
        )}
      </div>
    </div>
  );
}



function RemoveOverlayModal({ video, currentTime, videoSrc, onClose, onSaved }) {
  const previewRef = useRef(null);
  const [regions, setRegions] = useState(video.overlay_removals || []);
  const [drawing, setDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [bbox, setBbox] = useState(null);
  const [regenerate, setRegenerate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [fillMode, setFillMode] = useState("blur");

  const setRegionMode = (i, mode) => {
    setRegions((rs) => rs.map((r, idx) => (idx === i ? { ...r, mode } : r)));
    setPreviewImg(null);
  };

  const generatePreview = async (regs) => {
    setPreviewBusy(true);
    try {
      const t = previewRef.current?.currentTime || currentTime || 0;
      // Hack: encode the time into the first region's `note` field
      const payload = {
        overlay_removals: (regs || regions).map((r, i) => i === 0 ? { ...r, note: `@${t}` } : r),
        regenerate_clips: false,
      };
      const r = await api.post(
        `/videos/${video.id}/overlay-preview`,
        payload,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(r.data);
      setPreviewImg(url);
    } catch (err) {
      toast.error("Preview failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setPreviewBusy(false);
    }
  };

  useEffect(() => {
    if (previewRef.current) {
      const v = previewRef.current;
      const onLoaded = () => { v.currentTime = currentTime || 0; };
      v.addEventListener("loadedmetadata", onLoaded);
      return () => v.removeEventListener("loadedmetadata", onLoaded);
    }
  }, [currentTime]);

  const onDown = (e) => {
    if (!drawing) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setDrawStart({ x, y });
    setBbox({ x, y, w: 0, h: 0 });
  };
  const onMove = (e) => {
    if (!drawStart) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = (e.clientX - rect.left) / rect.width;
    const cy = (e.clientY - rect.top) / rect.height;
    setBbox({
      x: Math.min(drawStart.x, cx),
      y: Math.min(drawStart.y, cy),
      w: Math.abs(cx - drawStart.x),
      h: Math.abs(cy - drawStart.y),
    });
  };
  const onUp = () => setDrawStart(null);

  const addRegion = () => {
    if (!bbox || bbox.w < 0.01 || bbox.h < 0.01) {
      toast.error("Draw a box around the overlay first");
      return;
    }
    setRegions([...regions, { ...bbox, mode: fillMode, note: "" }]);
    setBbox(null);
    setDrawing(false);
    setPreviewImg(null);
    toast.success(`Region added (${fillMode}) — click Preview Fix to verify`);
  };

  const removeRegion = (i) => { setRegions(regions.filter((_, idx) => idx !== i)); setPreviewImg(null); };

  const save = async () => {
    if (regions.length === 0 && !drawing) {
      // Allow saving 0 regions to clear
    }
    setSaving(true);
    try {
      await api.post(`/videos/${video.id}/overlay-removal`, {
        overlay_removals: regions,
        regenerate_clips: regenerate,
      });
      if (regenerate) {
        toast.success("Saved — re-rendering all clips with the fix applied");
      } else {
        toast.success("Saved — applies to all future clip generations");
      }
      onSaved();
    } catch (err) {
      toast.error("Save failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="remove-overlay-modal"
    >
      <div className="bg-stone-950 border border-orange-700 max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-800">
          <div className="font-display uppercase text-xl flex items-center gap-2">
            <Eraser size={16} className="text-orange-500" /> Remove Overlay
          </div>
          <button onClick={onClose} className="btn-secondary" data-testid="overlay-close"><X size={14} /></button>
        </div>

        <div className="px-4 py-3 text-xs text-stone-400 border-b border-stone-800 leading-relaxed">
          Pause on a frame where the overlay (watermark, crosshair, broadcast graphic) is visible. Click <span className="text-orange-400">Draw Box</span>, drag a rectangle around it, then <span className="text-orange-400">Add Region</span>. The fix is applied to <span className="text-stone-200">every clip generated from this video</span>.
        </div>

        <div
          className={`relative bg-black ${drawing ? "cursor-crosshair" : ""}`}
          onMouseDown={onDown}
          onMouseMove={onMove}
          onMouseUp={onUp}
          onMouseLeave={onUp}
        >
          {previewImg ? (
            <img
              src={previewImg}
              alt="overlay preview"
              className="w-full aspect-video object-contain bg-black"
              data-testid="overlay-preview-image"
            />
          ) : (
            <video
              ref={previewRef}
              src={videoSrc}
              controls={!drawing}
              playsInline
              className="w-full aspect-video bg-black pointer-events-auto"
              style={drawing ? { pointerEvents: "none" } : undefined}
              data-testid="overlay-preview-video"
            />
          )}
          {/* Existing saved regions (dim grey) */}
          {regions.map((r, i) => (
            <div
              key={i}
              className="absolute border-2 border-emerald-500/60 bg-emerald-500/15 pointer-events-none"
              style={{
                left: `${r.x * 100}%`,
                top:  `${r.y * 100}%`,
                width: `${r.w * 100}%`,
                height: `${r.h * 100}%`,
              }}
            >
              <div className="absolute -top-5 left-0 text-[10px] font-mono-tc text-emerald-400 bg-stone-950/90 px-1">
                region {i + 1}
              </div>
            </div>
          ))}
          {/* Current draft box */}
          {bbox && (
            <div
              className="absolute border-2 border-orange-500 bg-orange-500/15 pointer-events-none"
              style={{
                left: `${bbox.x * 100}%`,
                top:  `${bbox.y * 100}%`,
                width: `${bbox.w * 100}%`,
                height: `${bbox.h * 100}%`,
              }}
            />
          )}
        </div>

        <div className="p-4 space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-[10px] uppercase tracking-widest text-stone-500 mr-1">Fill</span>
            {["blur","pixelate","solid","delogo"].map((m) => (
              <button
                key={m}
                onClick={() => setFillMode(m)}
                className={`text-[10px] uppercase tracking-wider px-2 py-1 border transition-colors ${
                  fillMode === m
                    ? "border-orange-700 text-orange-300 bg-orange-900/20"
                    : "border-stone-800 text-stone-500 hover:text-stone-200 hover:border-stone-600"
                }`}
                title={
                  m === "blur"     ? "Heavy gaussian blur — always works, slight blur patch visible" :
                  m === "pixelate" ? "Mosaic blocks — fully hides anything underneath" :
                  m === "solid"    ? "Solid grey block — most obvious but bulletproof" :
                                     "Smart fill (delogo) — least visible when it works, but can leak on sharp contrasty overlays"
                }
                data-testid={`overlay-mode-${m}`}
              >
                {m}
              </button>
            ))}
            <div className="flex-1" />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => { setDrawing((d) => !d); if (!drawing) setBbox(null); setPreviewImg(null); }}
              className={`btn-secondary inline-flex items-center gap-1.5 ${drawing ? "!text-orange-500 !border-orange-700" : ""}`}
              data-testid="overlay-draw-toggle"
            >
              <Pencil size={12} /> {drawing ? "Drawing — click & drag" : "Draw Box"}
            </button>
            {bbox && drawing && (
              <button onClick={addRegion} className="btn-primary inline-flex items-center gap-1.5" data-testid="overlay-add-region">
                <Plus size={12} /> Add Region
              </button>
            )}
            <button
              onClick={() => generatePreview()}
              disabled={previewBusy || regions.length === 0}
              className="btn-secondary inline-flex items-center gap-1.5 hover:!border-orange-700 hover:!text-orange-400"
              title="Show this frame with the overlay fix applied — verify before re-rendering clips"
              data-testid="overlay-preview-btn"
            >
              {previewBusy ? <Loader2 size={12} className="animate-spin" /> : <Eraser size={12} />}
              {previewImg ? "Refresh Preview" : "Preview Fix"}
            </button>
            {previewImg && (
              <button
                onClick={() => { setPreviewImg(null); }}
                className="btn-secondary inline-flex items-center gap-1.5"
                data-testid="overlay-preview-clear"
              >
                <X size={12} /> Back to video
              </button>
            )}
            <div className="flex-1" />
            <span className="text-xs font-mono-tc text-stone-500 self-center">
              {regions.length} region{regions.length !== 1 ? "s" : ""}
            </span>
          </div>

          {regions.length > 0 && (
            <ul className="border border-stone-800 divide-y divide-stone-800" data-testid="overlay-region-list">
              {regions.map((r, i) => (
                <li key={i} className="flex items-center gap-2 px-3 py-2 text-xs font-mono-tc">
                  <span className="text-emerald-400">●</span>
                  <span className="text-stone-300 w-6">{i + 1}</span>
                  <span className="text-stone-500 flex-1 truncate">
                    x:{(r.x * 100).toFixed(1)}% y:{(r.y * 100).toFixed(1)}% · {(r.w * 100).toFixed(1)}×{(r.h * 100).toFixed(1)}%
                  </span>
                  <select
                    value={r.mode || "blur"}
                    onChange={(e) => setRegionMode(i, e.target.value)}
                    className="bg-stone-900 border border-stone-700 text-stone-300 text-[10px] px-1 py-0.5"
                    data-testid={`overlay-region-mode-${i}`}
                  >
                    <option value="blur">blur</option>
                    <option value="pixelate">pixelate</option>
                    <option value="solid">solid</option>
                    <option value="delogo">delogo</option>
                  </select>
                  <button
                    onClick={() => removeRegion(i)}
                    className="text-stone-500 hover:text-rose-400"
                    data-testid={`overlay-remove-region-${i}`}
                  >
                    <X size={12} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <label className="flex items-start gap-2 text-sm text-stone-300 cursor-pointer">
            <input
              type="checkbox"
              checked={regenerate}
              onChange={(e) => setRegenerate(e.target.checked)}
              className="mt-1"
              data-testid="overlay-regen-checkbox"
            />
            <span>
              <span className="font-bold">Re-render all existing clips</span>
              <span className="text-stone-500 block text-xs mt-0.5">
                Already-rendered clips from this video will be deleted and regenerated with the overlay fix applied. Takes a few minutes.
              </span>
            </span>
          </label>
        </div>

        <div className="flex gap-2 p-4 pt-0">
          <button onClick={onClose} className="btn-secondary flex-1" data-testid="overlay-cancel">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            className="btn-primary flex-1"
            data-testid="overlay-save"
          >
            {saving ? "Saving…" : regions.length === 0 ? "Clear all regions" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Multi-picker modal → creates one "Master" clip per selected Association /
 * Performance, and links all produced clips to every selected Gallery.
 * The master reuses the source video's S3 object (no re-encode, no size cap).
 */
function PublishMasterModal({ video, onClose, onPublished }) {
  const [tab, setTab] = useState("associations");
  const [associations, setAssociations] = useState([]);
  const [performances, setPerformances] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickedA, setPickedA] = useState(new Set(video?.association ? [video.association] : []));
  const [pickedP, setPickedP] = useState(new Set(video?.event_name ? [video.event_name] : []));
  const [pickedG, setPickedG] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [a, p, g] = await Promise.all([
          api.get("/associations"),
          api.get("/events"),
          api.get("/galleries").catch(() => ({ data: { galleries: [] } })),
        ]);
        setAssociations((a.data.associations || []).map((x) => ({ name: x.name, count: x.clip_count || 0 })));
        setPerformances((p.data.events || []).map((x) => ({ name: x.name, count: x.clip_count || 0 })));
        setGalleries((g.data.galleries || []).map((x) => ({ id: x.id, name: x.name })));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const totalPicked = pickedA.size + pickedP.size + pickedG.size;

  const publish = async () => {
    if (totalPicked === 0) {
      toast.error("Pick at least one association, performance, or gallery");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/videos/${video.id}/publish-master`, {
        associations: Array.from(pickedA),
        performances: Array.from(pickedP),
        gallery_ids: Array.from(pickedG),
      });
      const created = r.data.created_master_clip_ids?.length || 0;
      const reused = r.data.reused_master_clip_ids?.length || 0;
      if (created && reused) toast.success(`Published master — ${created} new, ${reused} already existed`);
      else if (created) toast.success(`Published master to ${created} target${created === 1 ? "" : "s"}`);
      else if (reused) toast.success(`Master already published — updated gallery links`);
      onPublished();
    } catch (err) {
      toast.error("Publish failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const rows = tab === "associations" ? associations : tab === "performances" ? performances : galleries;
  const picked = tab === "associations" ? pickedA : tab === "performances" ? pickedP : pickedG;
  const setPicked = tab === "associations" ? setPickedA : tab === "performances" ? setPickedP : setPickedG;
  const filtered = q.trim()
    ? rows.filter((r) => (r.name || "").toLowerCase().includes(q.trim().toLowerCase()))
    : rows;
  const toggle = (id) => {
    setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
    setQ("");
  };

  return (
    <div
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="publish-master-modal"
    >
      <div className="bg-stone-950 border border-amber-700 max-w-2xl w-full max-h-[92vh] flex flex-col relative" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-2 right-2 z-10 bg-black/80 hover:bg-black text-white rounded-full p-1.5 border border-stone-700"
          data-testid="publish-master-close"
          aria-label="Close"
        >
          <X size={13} />
        </button>
        <div className="px-5 py-4 border-b border-stone-800 pr-14 shrink-0">
          <div className="label-tiny text-amber-500">Publish master recording</div>
          <div className="font-display uppercase text-xl truncate">{video?.original_name || "Untitled"}</div>
          <div className="text-[11px] text-stone-500 mt-0.5">
            The full source will appear as a MASTER clip under every target you pick — stream + download at native quality.
          </div>
        </div>

        <div className="px-5 py-2 border-b border-stone-800 shrink-0 flex gap-2">
          {[
            { key: "associations", label: `Associations (${pickedA.size})` },
            { key: "performances", label: `Performances (${pickedP.size})` },
            { key: "galleries", label: `Galleries (${pickedG.size})` },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setQ(""); }}
              className={`btn-secondary !py-1 text-xs ${tab === t.key ? "!text-amber-400 !border-amber-700" : ""}`}
              data-testid={`publish-master-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="px-5 py-2 border-b border-stone-800 shrink-0">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${tab}…`}
            className="field-input w-full !py-1.5 text-xs"
            data-testid="publish-master-search"
          />
        </div>

        <div className="flex-1 min-h-[220px] overflow-y-auto p-2">
          {loading ? (
            <div className="text-stone-500 text-sm p-3 inline-flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-stone-500 text-xs p-4 text-center">
              {q ? `No ${tab} match "${q}"` : `No ${tab} yet.`}
            </div>
          ) : (
            filtered.map((r) => {
              const id = r.id || r.name;
              const on = picked.has(id);
              return (
                <button
                  key={id}
                  onClick={() => toggle(id)}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-stone-900 ${on ? "text-amber-300" : "text-stone-300"}`}
                  data-testid={`publish-master-row-${tab}-${id.toString().replace(/\s+/g, "_")}`}
                >
                  <span className={`w-4 h-4 border ${on ? "border-amber-500 bg-amber-600/30" : "border-stone-600"} inline-flex items-center justify-center shrink-0`}>
                    {on && <Check size={11} className="text-amber-400" />}
                  </span>
                  <span className="truncate flex-1">{r.name}</span>
                  {r.count != null && <span className="text-[10px] font-mono-tc text-stone-500 shrink-0">{r.count}</span>}
                </button>
              );
            })
          )}
        </div>

        <div className="px-5 py-3 border-t border-stone-800 shrink-0 flex items-center gap-2">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mr-auto">
            {totalPicked} target{totalPicked === 1 ? "" : "s"} selected
          </div>
          <button onClick={onClose} className="btn-secondary" data-testid="publish-master-cancel">Cancel</button>
          <button
            onClick={publish}
            disabled={saving || totalPicked === 0}
            className="btn-primary inline-flex items-center gap-1.5 !bg-amber-600 hover:!bg-amber-500"
            data-testid="publish-master-save"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Video size={13} />}
            Publish Master
          </button>
        </div>
      </div>
    </div>
  );
}



/**
 * Batch "Send All Clips" modal — takes every rendered clip in the current
 * Studio session and assigns them all at once to a chosen Association +
 * Performance + Galleries. Reuses the same tabbed UI as PublishMasterModal
 * but fans out PATCH /clips/:id calls (association, event_name,
 * performance_name) plus POST /galleries/:id/clips for each picked gallery.
 */
function SendAllClipsModal({ video, clips, onClose, onSaved }) {
  const [tab, setTab] = useState("associations");
  const [associations, setAssociations] = useState([]);
  const [performances, setPerformances] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickedA, setPickedA] = useState(video?.association || "");
  const [pickedP, setPickedP] = useState(video?.event_name || "");
  const [pickedG, setPickedG] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [a, p, g] = await Promise.all([
          api.get("/associations"),
          api.get("/events"),
          api.get("/galleries").catch(() => ({ data: { galleries: [] } })),
        ]);
        setAssociations((a.data.associations || []).map((x) => ({ name: x.name, count: x.clip_count || 0 })));
        setPerformances((p.data.events || []).map((x) => ({ name: x.name, count: x.clip_count || 0 })));
        setGalleries((g.data.galleries || []).map((x) => ({ id: x.id, name: x.name })));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const clipCount = clips.length;
  const anyPicked = Boolean(pickedA || pickedP || pickedG.size > 0);

  const send = async () => {
    if (!anyPicked) {
      toast.error("Pick at least one association, performance, or gallery");
      return;
    }
    setSaving(true);
    setProgress({ done: 0, total: clipCount });
    const patchPayload = {};
    if (pickedA !== "") patchPayload.association = pickedA;
    if (pickedP !== "") {
      patchPayload.event_name = pickedP;
      patchPayload.performance_name = pickedP;
    }
    let okAssign = 0, failAssign = 0;
    for (const c of clips) {
      try {
        if (Object.keys(patchPayload).length > 0) {
          await api.patch(`/clips/${c.id}`, patchPayload);
        }
        okAssign += 1;
      } catch {
        failAssign += 1;
      }
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    let okGal = 0, failGal = 0;
    for (const gid of Array.from(pickedG)) {
      for (const c of clips) {
        try {
          await api.post(`/galleries/${gid}/clips`, { clip_ids: [c.id] });
          okGal += 1;
        } catch {
          failGal += 1;
        }
      }
    }
    const bits = [];
    if (okAssign) bits.push(`${okAssign} clip${okAssign === 1 ? "" : "s"} tagged`);
    if (pickedG.size > 0 && okGal) bits.push(`${okGal} added to ${pickedG.size} galler${pickedG.size === 1 ? "y" : "ies"}`);
    if (failAssign || failGal) bits.push(`${failAssign + failGal} failed`);
    toast.success(bits.length ? bits.join(" · ") : "Nothing to update");
    setSaving(false);
    onSaved();
  };

  const rows = tab === "associations" ? associations : tab === "performances" ? performances : galleries;
  const filtered = q.trim()
    ? rows.filter((r) => (r.name || "").toLowerCase().includes(q.trim().toLowerCase()))
    : rows;

  const toggle = (id) => {
    if (tab === "associations") setPickedA(pickedA === id ? "" : id);
    else if (tab === "performances") setPickedP(pickedP === id ? "" : id);
    else setPickedG((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
    if (tab !== "galleries") setQ("");
  };

  const aLabel = pickedA ? `Assoc: ${pickedA}` : null;
  const pLabel = pickedP ? `Perf: ${pickedP}` : null;
  const gLabel = pickedG.size > 0 ? `${pickedG.size} galler${pickedG.size === 1 ? "y" : "ies"}` : null;
  const summary = [aLabel, pLabel, gLabel].filter(Boolean).join(" · ") || "Nothing picked yet";

  return (
    <div
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="send-all-clips-modal"
    >
      <div className="bg-stone-950 border border-sky-700 max-w-2xl w-full max-h-[92vh] flex flex-col relative" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-2 right-2 z-10 bg-black/80 hover:bg-black text-white rounded-full p-1.5 border border-stone-700"
          data-testid="send-all-clips-close"
          aria-label="Close"
        >
          <X size={13} />
        </button>
        <div className="px-5 py-4 border-b border-stone-800 pr-14 shrink-0">
          <div className="label-tiny text-sky-400">Send every rendered clip in this session</div>
          <div className="font-display uppercase text-xl truncate">{video?.original_name || "Untitled"}</div>
          <div className="text-[11px] text-stone-500 mt-0.5">
            {clipCount} clip{clipCount === 1 ? "" : "s"} will be tagged with the same
            Association / Performance and (optionally) added to the picked Galleries.
          </div>
          <div className="text-[10px] uppercase tracking-widest text-sky-400/80 mt-2 font-mono-tc">{summary}</div>
        </div>

        <div className="px-5 py-2 border-b border-stone-800 shrink-0 flex gap-2">
          {[
            { key: "associations", label: `Association${pickedA ? " \u2713" : ""}` },
            { key: "performances", label: `Performance${pickedP ? " \u2713" : ""}` },
            { key: "galleries", label: `Galleries (${pickedG.size})` },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setQ(""); }}
              className={`btn-secondary !py-1 text-xs ${tab === t.key ? "!text-sky-400 !border-sky-700" : ""}`}
              data-testid={`send-all-clips-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="px-5 py-2 border-b border-stone-800 shrink-0">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${tab}\u2026`}
            className="field-input w-full !py-1.5 text-xs"
            data-testid="send-all-clips-search"
          />
        </div>

        <div className="flex-1 min-h-[220px] overflow-y-auto p-2">
          {loading ? (
            <div className="text-stone-500 text-sm p-3 inline-flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-stone-500 text-xs p-4 text-center">
              {q ? `No ${tab} match "${q}"` : `No ${tab} yet.`}
            </div>
          ) : (
            filtered.map((r) => {
              const id = r.id || r.name;
              const on = tab === "associations"
                ? pickedA === id
                : tab === "performances"
                  ? pickedP === id
                  : pickedG.has(id);
              return (
                <button
                  key={id}
                  onClick={() => toggle(id)}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-stone-900 ${on ? "text-sky-300" : "text-stone-300"}`}
                  data-testid={`send-all-clips-row-${tab}-${id.toString().replace(/\s+/g, "_")}`}
                >
                  <span className={`w-4 h-4 border ${on ? "border-sky-500 bg-sky-600/30" : "border-stone-600"} inline-flex items-center justify-center shrink-0`}>
                    {on && <Check size={11} className="text-sky-400" />}
                  </span>
                  <span className="truncate flex-1">{r.name}</span>
                  {r.count != null && <span className="text-[10px] font-mono-tc text-stone-500 shrink-0">{r.count}</span>}
                </button>
              );
            })
          )}
        </div>

        <div className="px-5 py-3 border-t border-stone-800 shrink-0 flex items-center gap-2">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mr-auto">
            {saving
              ? `Sending ${progress.done}/${progress.total}\u2026`
              : `${clipCount} clip${clipCount === 1 ? "" : "s"} ready to send`}
          </div>
          <button onClick={onClose} disabled={saving} className="btn-secondary disabled:opacity-50" data-testid="send-all-clips-cancel">Cancel</button>
          <button
            onClick={send}
            disabled={saving || !anyPicked || clipCount === 0}
            className="btn-primary inline-flex items-center gap-1.5 !bg-sky-600 hover:!bg-sky-500"
            data-testid="send-all-clips-save"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <FolderHeart size={13} />}
            Send {clipCount} Clip{clipCount === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>
  );
}
